/* ============================================================
   RFQ Form — production
   Lives at /pages/quote (sections/rfq-form.liquid).
   Vanilla JS, no bundler.

   - SKU fast-path is the headline
   - Cascade Material → Shape → Size&Grade as fallback
   - Sourcing fallback for unstocked dims
   - Multi-line with sticky right cart on desktop
   - PDP prefill via ?product=<handle>&qty=<n>&sku=<n>

   Visual: 1:1 with live PDP (.dp-configurator).
   Backend payload: schema_version 3.2.0 when every catalog line was built
   from a v2 catalog (see buildPayload); 3.1.0 otherwise, so legacy-built
   lines keep the backend's lenient validation. Backend accepts both.
   Catalog format v2 (top-level format_version: 2) drives per-axis size
   pickers that pin exactly ONE product; an older cached catalog (CDN lag)
   feature-detects back to the combined Size & Grade combo.
   ============================================================ */
(function () {
  'use strict';

  var ROOT = document.querySelector('[data-rfq]');
  if (!ROOT) return;

  var INGEST_URL = ROOT.getAttribute('data-ingest-url') || 'https://rfq.metalsrus.ca/submit';

  // Active storefront language ('fr' on /fr, 'en' otherwise). Injected by the
  // section as data-lang ({{ request.locale.iso_code }}); falls back to the
  // <html lang> attribute. Sent on the submission so the dashboard sets the
  // conversation language and the quote + invoice + docs follow it (Bill 96).
  var PAGE_LANG = (ROOT.getAttribute('data-lang') || document.documentElement.lang || '').slice(0, 2).toLowerCase();

  // ---------- i18n (P2) ----------
  // Strings the JS renders at runtime, injected from Liquid (locales/*.json) as
  // window.MRU_RFQ_FORM_I18N. EN fallbacks keep the UI intact if the dict is
  // missing. t(key, fallback, vars) fills {token} placeholders.
  var T = (typeof window !== 'undefined' && window.MRU_RFQ_FORM_I18N) || {};
  // Shopify's `| t` HTML-escapes its output ('->&#39;, &->&amp;, etc.), so the injected
  // dict carries entities. The values are inserted via textContent in places, where entities
  // show literally. Decode them once here (&amp; last) so every read is clean text.
  for (var _k in T) {
    if (typeof T[_k] === 'string') {
      T[_k] = T[_k].replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
    }
  }
  function t(key, fallback, vars) {
    var s = (T && T[key] != null) ? T[key] : fallback;
    if (vars) {
      for (var k in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, k)) {
          s = String(s).split('{' + k + '}').join(String(vars[k]));
        }
      }
    }
    return s;
  }
  // Plural helper: picks the *_one / *_other form by count, then fills {n}.
  function tn(keyOne, keyOther, fbOne, fbOther, n) {
    return n === 1 ? t(keyOne, fbOne, { n: n }) : t(keyOther, fbOther, { n: n });
  }

  // --- material code map (catalog material id → 2-letter shop code) ---
  var MATERIAL_CODES = {
    'aluminum': 'AL',
    'carbon-steel': 'CS', 'carbon steel': 'CS', 'steel': 'CS',
    'stainless-steel': 'SS', 'stainless steel': 'SS', 'stainless': 'SS',
    'brass': 'BR',
    'copper': 'CU',
    'cast-iron': 'CI', 'cast iron': 'CI',
    'tool-steel': 'TS', 'tool steel': 'TS',
  };
  function codeFor(matId, matLabel) {
    var k = (matId || matLabel || '').toLowerCase().replace(/_/g, '-');
    return MATERIAL_CODES[k] || (matLabel || matId || 'OT').slice(0, 2).toUpperCase();
  }

  // --- shape cross-section icons (rendered into the resolved-product thumb) ---
  var SHAPE_ICONS = {
    'angle':           '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 4h4v11h11v4H5z"/></svg>',
    'channel':         '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 4h14v4h-10v8h10v4H5z"/></svg>',
    'drill-rod':       '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="9"/></svg>',
    'flat-bar':        '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="3" width="12" height="18"/></svg>',
    'h-beam':          '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h5v6h6V4h5v16h-5v-6h-6v6H4z"/></svg>',
    'hex-bar':         '<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="12,3 21,8 21,16 12,21 3,16 3,8"/></svg>',
    'i-beam':          '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v3h-6v10h6v3H4v-3h6V7H4z"/></svg>',
    'mechanical-tube': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><circle cx="12" cy="12" r="8"/></svg>',
    'pipe':            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><circle cx="12" cy="12" r="8"/></svg>',
    'plate':           '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="9" width="18" height="6"/></svg>',
    'rectangle-tube':  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><rect x="3" y="6" width="18" height="12"/></svg>',
    'rotocast-tubing': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><circle cx="12" cy="12" r="8"/></svg>',
    'round-bar':       '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="9"/></svg>',
    'round-tube':      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><circle cx="12" cy="12" r="8"/></svg>',
    'sheet':           '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="11" width="18" height="2"/></svg>',
    'square-bar':      '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16"/></svg>',
    'square-tube':     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><rect x="4" y="4" width="16" height="16"/></svg>',
    't-bar':           '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v4h-6v12h-4V8H4z"/></svg>',
    'threaded-rod':    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="9"/></svg>',
    'tube':            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><circle cx="12" cy="12" r="8"/></svg>'
  };
  function shapeIcon(shapeId) {
    return SHAPE_ICONS[shapeId] || '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="9" width="16" height="6"/></svg>';
  }

  // ---------- state ----------
  var state = {
    catalog: null,
    catalogStatus: 'loading',
    skuIndex: null,                    // { '4012': { material, shape, grade, product } }
    handleIndex: null,                 // same shape, keyed by product handle
    user: null,
    step: 'lines',                     // lines | customer | submission
    lines: [],
    activeLine: null,
    customer: { customer_status: 'New', first_name: '', last_name: '', email: '', phone: '', business_name: '', ship_to_province: '' },
    files: [],                         // File objects attached in the submission step (not draft-persisted)
    submission: {
      timeline: 'Flexible',
      specific_date: '',
      fulfillment_method: 'pickup',
      shipping_address: {
        first_name: '', last_name: '', company: '',
        address_line_1: '', address_line_2: '',
        city: '', province: '', postal_code: '',
        country: 'Canada',
      },
      notes: '',
      cert_required: false,
      recurring: false,
      terms: false,
    },
    prefill: null,
  };

  // ---------- DOM ----------
  var DOM = {
    error: ROOT.querySelector('[data-rfq-error]'),
    builder: ROOT.querySelector('[data-rfq-builder]'),
    skuZone: ROOT.querySelector('[data-rfq-sku-zone]'),
    build: ROOT.querySelector('[data-rfq-build]'),
    or: ROOT.querySelector('.rfq-or'),
    committed: ROOT.querySelector('[data-rfq-committed]'),
    lines: ROOT.querySelector('[data-rfq-lines]'),
    committedCount: ROOT.querySelector('[data-rfq-committed-count]'),
    customer: ROOT.querySelector('[data-rfq-customer]'),
    customerBody: ROOT.querySelector('[data-rfq-customer-body]'),
    submission: ROOT.querySelector('[data-rfq-submission]'),
    submissionBody: ROOT.querySelector('[data-rfq-submission-body]'),
    submitZone: ROOT.querySelector('[data-rfq-submit-zone]'),
    submitBtn: ROOT.querySelector('[data-rfq-submit]'),
    submitNote: ROOT.querySelector('[data-rfq-submit-note]'),
    termsCheck: ROOT.querySelector('[data-rfq-terms]'),
    done: ROOT.querySelector('[data-rfq-done]'),
    doneRef: ROOT.querySelector('[data-rfq-done-ref]'),
    stub: ROOT.querySelector('[data-rfq-stub]'),
    tkRows: ROOT.querySelector('[data-rfq-ticket-rows]'),
    tkEmpty: ROOT.querySelector('[data-rfq-ticket-empty]'),
    tkTotal: ROOT.querySelector('[data-rfq-ticket-total]'),
    tkNum: ROOT.querySelector('[data-rfq-ticket-num]'),
    tkAddAnother: ROOT.querySelector('[data-rfq-add-another]'),
    tallyCounts: ROOT.querySelectorAll('[data-rfq-tally-count]'),
    tallySub: ROOT.querySelector('[data-rfq-tally-sub]'),
    prefill: ROOT.querySelector('[data-rfq-prefill]'),
    prefillText: ROOT.querySelector('[data-rfq-prefill-text]'),
    prefillStamp: ROOT.querySelector('[data-rfq-prefill-stamp]'),
    prefillChange: ROOT.querySelector('[data-rfq-prefill-change]'),
    mobileBtn: ROOT.querySelector('[data-rfq-mobile-btn]'),
  };

  // ---------- helpers ----------
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]; }); }

  // ---------- draft autosave + request history (RFQ v2, 2026-06) ----------
  // Draft: the whole in-progress request survives refresh / nav-away / a
  // mobile app-switch. Saved debounced on every interaction; cleared on
  // successful submit. History: the last submitted request's lines power
  // the "Add from your last request" chips on the next visit.
  var DRAFT_KEY = 'mru_rfq_draft_v1';
  var HISTORY_KEY = 'mru_rfq_history_v1';
  var DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  var DRAFT_MAX_BYTES = 400 * 1024;               // stay far from the 5MB cap

  function lsGet(key) {
    try { return JSON.parse(window.localStorage.getItem(key) || 'null'); }
    catch (e) { return null; }
  }
  function lsSet(key, value) {
    try {
      var s = JSON.stringify(value);
      if (s.length > DRAFT_MAX_BYTES) return false;
      window.localStorage.setItem(key, s);
      return true;
    } catch (e) { return false; }
  }
  function lsDel(key) { try { window.localStorage.removeItem(key); } catch (e) {} }

  function saveDraft() {
    // Nothing meaningful yet → keep storage clean.
    var hasContent = state.lines.length > 0 || state.customer.email ||
      state.customer.first_name || state.customer.business_name;
    if (!hasContent) { lsDel(DRAFT_KEY); return; }
    lsSet(DRAFT_KEY, {
      at: Date.now(),
      lines: state.lines,
      customer: state.customer,
      submission: state.submission,
    });
    queueServerDraft();
  }
  var _draftTimer = null;
  function saveDraftSoon() {
    if (_draftTimer) clearTimeout(_draftTimer);
    _draftTimer = setTimeout(function () { _draftTimer = null; saveDraft(); }, 600);
  }

  // ---------- server-side draft (lead capture, v5) ----------
  // Once we know the visitor's email, mirror the in-progress request to the
  // dashboard so an abandoned form becomes a followable lead instead of a
  // silent loss. Strictly best-effort: throttled, deduped, never surfaces
  // an error, and the backend wipes it the moment a real submission lands.
  var DRAFT_ENDPOINT = INGEST_URL.replace(/\/submit\/?$/, '/draft');
  var _serverDraftTimer = null;
  var _serverDraftLast = '';

  function serverDraftPayload() {
    var c = state.customer;
    if (!c.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) return null;
    return {
      email: c.email,
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      phone: c.phone || '',
      business_name: c.business_name || '',
      ship_to_province: c.ship_to_province || '',
      items: state.lines.map(summarizeLine).filter(Boolean),
      notes: (state.submission.notes || '').slice(0, 500),
      source: 'rfq_form',
    };
  }

  function queueServerDraft() {
    if (_serverDraftTimer) clearTimeout(_serverDraftTimer);
    _serverDraftTimer = setTimeout(function () {
      _serverDraftTimer = null;
      postServerDraft(false);
    }, 3000);
  }

  var _serverDraftDisabled = false; // flipped on successful submit — the
                                    // backend just cleared the lead; a queued
                                    // timer must not resurrect it.
  function postServerDraft(useBeacon) {
    if (_serverDraftDisabled) return;
    var payload = serverDraftPayload();
    if (!payload) return;
    var body = JSON.stringify(payload);
    if (body === _serverDraftLast) return; // nothing new to say
    _serverDraftLast = body;
    try {
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(DRAFT_ENDPOINT, new Blob([body], { type: 'application/json' }));
        return;
      }
      fetch(DRAFT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
      }).catch(function () { /* lead capture must never bother the customer */ });
    } catch (e) { /* ditto */ }
  }

  // Last-chance flush when they leave mid-build.
  window.addEventListener('pagehide', function () { postServerDraft(true); });

  function loadDraft() {
    var d = lsGet(DRAFT_KEY);
    if (!d || !d.at || (Date.now() - d.at) > DRAFT_MAX_AGE_MS) return null;
    if (!Array.isArray(d.lines)) return null;
    return d;
  }

  // One-line human summary of a committed line, for banner + history chips.
  function summarizeLine(l) {
    if (!l) return '';
    if (l.mode === 'freeform') {
      return (l.freeformText || t('listItem', 'List item')).slice(0, 60);
    }
    if (l.mode === 'sourcing') {
      var bits = [l.sourcingMaterial, l.sourcingShape, l.sourcingDimensions].filter(Boolean).join(' ');
      return (bits || l.sourcingDescription || t('sourcingCustom', 'Custom item')).slice(0, 60) + ' ×' + (l.quantity || 1);
    }
    if (l.pinned && l.pinned.title) {
      return String(l.pinned.title).slice(0, 60) + ' ×' + (l.quantity || 1);
    }
    var mat = l.material && l.material.label ? l.material.label : '';
    var shp = l.shape && l.shape.label ? l.shape.label : '';
    var len = l.length ? (l.length + '"') : '';
    return [mat, shp, len].filter(Boolean).join(' ').slice(0, 60) + ' ×' + (l.quantity || 1);
  }

  // Host element for the restore banner + history chips, injected above the
  // builder. Created once, reused.
  function noticeHost() {
    var host = ROOT.querySelector('[data-rfq-notice-host]');
    if (host) return host;
    host = document.createElement('div');
    host.setAttribute('data-rfq-notice-host', '');
    var anchor = DOM.builder || ROOT.firstChild;
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(host, anchor);
    else ROOT.insertBefore(host, ROOT.firstChild);
    return host;
  }

  function offerDraftRestore(draft) {
    var host = noticeHost();
    var n = draft.lines.length;
    var summary = draft.lines.slice(0, 2).map(summarizeLine).filter(Boolean).join(' · ');
    var restoreBody = n
      ? tn('restoreWithOne', 'restoreWithOther',
          'You have an unfinished request with {n} item', 'You have an unfinished request with {n} items', n)
      : t('restoreLead2', 'You have an unfinished request');
    host.innerHTML =
      '<div class="rfq-notice" data-rfq-restore>' +
        '<div class="rfq-notice__text">' +
          '<strong>' + esc(t('restoreLead', 'Pick up where you left off?')) + '</strong> ' +
          restoreBody +
          (summary ? ', ' + esc(summary) : '') + '.' +
        '</div>' +
        '<div class="rfq-notice__actions">' +
          '<button type="button" class="rfq-notice__btn rfq-notice__btn--primary" data-rfq-restore-yes>' + esc(t('restoreYes', 'Restore')) + '</button>' +
          '<button type="button" class="rfq-notice__btn" data-rfq-restore-no>' + esc(t('restoreNo', 'Discard')) + '</button>' +
        '</div>' +
      '</div>';
    host.querySelector('[data-rfq-restore-yes]').addEventListener('click', function () {
      state.lines = draft.lines || [];
      if (draft.customer) state.customer = Object.assign({}, state.customer, draft.customer);
      if (draft.submission) {
        state.submission = Object.assign({}, state.submission, draft.submission);
        state.submission.terms = false; // terms are re-accepted per submission
      }
      if (!state.lines.length) state.activeLine = state.activeLine || newLine();
      host.innerHTML = '';
      render();
      renderCustomer();
      renderSubmission();
      renderSubmitState();
    });
    host.querySelector('[data-rfq-restore-no]').addEventListener('click', function () {
      lsDel(DRAFT_KEY);
      host.innerHTML = '';
      renderHistoryChips(); // a discarded draft can still surface last-request chips
    });
  }

  function renderHistoryChips() {
    var hist = lsGet(HISTORY_KEY);
    if (!hist || !Array.isArray(hist.lines) || !hist.lines.length) return;
    var host = noticeHost();
    if (host.querySelector('[data-rfq-restore]')) return; // restore banner wins
    var chips = hist.lines.slice(0, 6).map(function (l, i) {
      return '<button type="button" class="rfq-history__chip" data-rfq-history-add="' + i + '">+ ' + esc(summarizeLine(l)) + '</button>';
    }).join('');
    host.innerHTML =
      '<div class="rfq-history">' +
        '<span class="rfq-history__label">' + esc(t('historyLabel', 'Add from your last request')) + (hist.ref ? ' (' + esc(hist.ref) + ')' : '') + ':</span>' +
        chips +
        '<button type="button" class="rfq-history__dismiss" data-rfq-history-dismiss title="' + esc(t('historyHide', 'Hide')) + '">×</button>' +
      '</div>';
    host.querySelectorAll('[data-rfq-history-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-rfq-history-add'), 10);
        var src = hist.lines[idx];
        if (!src) return;
        var copy = JSON.parse(JSON.stringify(src));
        copy.id = 'l_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
        state.lines.push(copy);
        render();
        renderSubmitState();
        saveDraftSoon();
        btn.disabled = true;
        btn.classList.add('is-added');
        btn.textContent = t('historyAdded', '✓ Added');
      });
    });
    host.querySelector('[data-rfq-history-dismiss]').addEventListener('click', function () {
      host.innerHTML = '';
    });
  }

  function saveHistory(refNumber) {
    if (!state.lines.length) return;
    lsSet(HISTORY_KEY, {
      at: Date.now(),
      ref: refNumber || '',
      lines: state.lines.slice(0, 10),
    });
  }
  function fmtDim(v) {
    if (v == null || v === '') return '';
    var n = parseFloat(v);
    if (isNaN(n)) return String(v);
    return n.toFixed(3).replace(/\.?0+$/, '') + '"';
  }
  // Customers type cut sizes the way the shop talks: 36, 36.5, 1/2, 1-1/2,
  // 1 1/2, 24". parseFloat would silently truncate '1-1/2' to 1 and put a
  // wrong cut on the quote, so fractions are parsed properly here. Anything
  // unparseable returns NaN and the field errors exactly like before.
  function parseInches(raw) {
    if (raw == null) return NaN;
    var s = String(raw).trim().replace(/(?:"|″|”|\s|in\.?|po\.?)+$/i, '').trim();
    if (!s) return NaN;
    var m = s.match(/^(\d+)[\s-]+(\d+)\s*\/\s*(\d+)$/); // 1-1/2 or 1 1/2
    if (!m) {
      m = s.match(/^(\d+)\s*\/\s*(\d+)$/);              // 3/8
      if (m) m = [m[0], '0', m[1], m[2]];
    }
    if (m) {
      var den = parseInt(m[3], 10);
      if (!den) return NaN;
      var v = parseInt(m[1], 10) + parseInt(m[2], 10) / den;
      return isFinite(v) ? v : NaN;
    }
    return /^\d*\.?\d+$/.test(s) ? parseFloat(s) : NaN;
  }
  function showError(msg) {
    if (!DOM.error) return;
    DOM.error.textContent = msg;
    DOM.error.hidden = false;
    DOM.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  function clearError() { if (DOM.error) { DOM.error.hidden = true; DOM.error.textContent = ''; } }

  function prettyAxis(a) {
    return ({
      diameter: t('axisDiameter', 'Diameter'),
      thickness: t('axisThickness', 'Thickness'),
      wall: t('axisWall', 'Wall'),
      wall_thickness: t('axisWallThickness', 'Wall thickness'),
      od: t('axisOd', 'OD'),
      leg_1: t('axisLeg1', 'Leg 1'),
      leg_2: t('axisLeg2', 'Leg 2'),
      height: t('axisHeight', 'Height'),
      width: t('axisWidth', 'Width')
    })[a] || a.replace(/_/g, ' ');
  }
  // Required shipping fields when fulfillment_method === 'include_shipping'.
  // First/last/address1/city/province/postal/country are all required; company
  // + address2 are optional. Empty / whitespace-only counts as missing.
  function shippingAddressComplete(a) {
    if (!a || typeof a !== 'object') return false;
    var REQ = ['first_name', 'last_name', 'address_line_1', 'city', 'province', 'postal_code', 'country'];
    for (var i = 0; i < REQ.length; i++) {
      if (!a[REQ[i]] || !String(a[REQ[i]]).trim()) return false;
    }
    return true;
  }

  // ---------- catalog access ----------
  function getMaterials() { return (state.catalog && state.catalog.materials) || []; }
  function findMaterial(id) { return getMaterials().filter(function (m) { return m.id === id; })[0]; }
  function findShape(matId, shapeId) {
    var m = findMaterial(matId);
    return m ? (m.shapes || []).filter(function (s) { return s.id === shapeId; })[0] : null;
  }
  function defaultGrade(shape) { return (shape.grades || [])[0] || null; }

  // ---------- catalog format v2 (per-axis pickers) ----------
  // v2 shape nodes carry required_axes: [{key,label,label_fr,unit}] and
  // products carry axis_values for every required axis (axis_incomplete: true
  // when the value couldn't be derived). Everything below feature-detects, so
  // a stale cached v1 catalog keeps the whole legacy path working.
  function catalogV2() {
    return !!(state.catalog && parseFloat(state.catalog.format_version) >= 2);
  }
  function lineIsV2(line) {
    return !!(line && line.shape && Array.isArray(line.shape.required_axes));
  }
  function axisLabelOf(ax) {
    if (PAGE_LANG === 'fr' && ax.label_fr) return ax.label_fr;
    return ax.label || ax.key;
  }
  function prettyAxisForLine(line, key) {
    if (lineIsV2(line)) {
      var axes = line.shape.required_axes;
      for (var i = 0; i < axes.length; i++) {
        if (axes[i].key === key) return axisLabelOf(axes[i]);
      }
    }
    return prettyAxis(key);
  }
  function forEachShapeProduct(shape, fn) {
    ((shape && shape.grades) || []).forEach(function (g) {
      (g.products || []).forEach(function (p) { fn(g, p); });
    });
  }
  function flatShapeProducts(shape) {
    var out = [];
    forEachShapeProduct(shape, function (g, p) { out.push({ grade: g, product: p }); });
    out.sort(function (a, b) { return String(a.product.title || '').localeCompare(String(b.product.title || '')); });
    return out;
  }
  function shapeHasIncomplete(shape) {
    var found = false;
    forEachShapeProduct(shape, function (g, p) { if (p.axis_incomplete) found = true; });
    return found;
  }
  function shapeCompleteCount(shape) {
    var n = 0;
    forEachShapeProduct(shape, function (g, p) { if (!p.axis_incomplete) n++; });
    return n;
  }
  function productMatchesAxes(p, axisValues) {
    var av = p.axis_values || {};
    return Object.keys(axisValues).every(function (a) {
      return parseFloat(av[a]) === parseFloat(axisValues[a]);
    });
  }
  // Values still pickable for axis N, given the picks on axes 0..N-1.
  // Only real combinations survive — the sets come from actual products,
  // not the unfiltered union. null = locked (an earlier axis isn't picked).
  function axisOptionsV2(shape, axisValues, axisIndex) {
    var axes = (shape && shape.required_axes) || [];
    var prior = {};
    for (var i = 0; i < axisIndex; i++) {
      var k = axes[i].key;
      var v = axisValues[k];
      if (v == null || isNaN(parseFloat(v))) return null;
      prior[k] = v;
    }
    var key = axes[axisIndex].key;
    var seen = {};
    var out = [];
    forEachShapeProduct(shape, function (g, p) {
      if (p.axis_incomplete) return;
      var val = parseFloat((p.axis_values || {})[key]);
      if (isNaN(val)) return;
      if (!productMatchesAxes(p, prior)) return;
      var sk = String(val);
      if (!seen[sk]) { seen[sk] = true; out.push(val); }
    });
    out.sort(function (a, b) { return a - b; });
    return out;
  }
  function availableGradesV2(shape, axisValues) {
    var out = [];
    ((shape && shape.grades) || []).forEach(function (g) {
      var has = (g.products || []).some(function (p) {
        return !p.axis_incomplete && productMatchesAxes(p, axisValues);
      });
      if (has) out.push(g);
    });
    return out;
  }
  function isPickByTitleShape(shape) {
    if (!shape) return false;
    if (shape.pick_by_title) return true;
    return Array.isArray(shape.required_axes) && shape.required_axes.length === 0;
  }
  // Title-picker mode: tiny/irregular shapes (beams, t-bar), an explicit
  // user toggle, or a shape whose products are all axis_incomplete.
  function isTitleModeLine(line) {
    if (!lineIsV2(line)) return false;
    if (isPickByTitleShape(line.shape)) return true;
    if (line.titlePicker) return true;
    return shapeCompleteCount(line.shape) === 0;
  }
  function axesComplete(shape, axisValues) {
    var axes = (shape && shape.required_axes) || [];
    return axes.every(function (ax) {
      var v = axisValues[ax.key];
      return v != null && !isNaN(parseFloat(v));
    });
  }
  function titlePickerEntries(line) {
    if (line.collision && line.collision.length) return line.collision;
    return flatShapeProducts(line.shape);
  }
  // v2 shape snapshot stored on the line (same fields the legacy snapshot
  // carries, plus the v2 markers when the catalog provides them).
  function shapeSnapshot(s) {
    return {
      id: s.id, label: s.label,
      stocked_axes: s.stocked_axes || [],
      available_dimension_values: s.available_dimension_values || {},
      grades: s.grades || [],
      required_axes: Array.isArray(s.required_axes) ? s.required_axes : null,
      pick_by_title: !!s.pick_by_title
    };
  }

  /** Build SKU index + handle index after catalog loads. */
  function buildIndices() {
    state.skuIndex = {};
    state.handleIndex = {};
    getMaterials().forEach(function (m) {
      (m.shapes || []).forEach(function (s) {
        (s.grades || []).forEach(function (g) {
          (g.products || []).forEach(function (p) {
            var ref = { material: m, shape: s, grade: g, product: p };
            if (p.sm3_sku) state.skuIndex[String(p.sm3_sku).toUpperCase()] = ref;
            if (p.handle) state.handleIndex[p.handle] = ref;
          });
        });
      });
    });
  }

  function findBySku(sku) {
    if (!state.skuIndex) return null;
    return state.skuIndex[String(sku).trim().toUpperCase()] || null;
  }
  function findByHandle(h) {
    if (!state.handleIndex) return null;
    return state.handleIndex[h] || null;
  }

  // gradeId is optional (v2 flow). When present the match is grade-filtered
  // and axis_incomplete products are skipped — those are only reachable
  // through the title picker, never through axis equality.
  function resolveSelection(shape, axisValues, gradeId) {
    var products = [];
    (shape.grades || []).forEach(function (g) {
      if (gradeId && g.id !== gradeId) return;
      (g.products || []).forEach(function (p) {
        if (gradeId && p.axis_incomplete) return;
        products.push({ grade: g, product: p });
      });
    });
    var matching = products.filter(function (item) {
      var av = item.product.axis_values || {};
      return Object.keys(axisValues).every(function (axis) {
        return parseFloat(av[axis]) === parseFloat(axisValues[axis]);
      });
    });
    var maxLen = 0, minLen = Infinity, isAlwaysRaq = false;
    matching.forEach(function (item) {
      var d = item.product.dimensions_available || {};
      if ((d.max_length || 0) > maxLen) maxLen = d.max_length || 0;
      if ((d.min_length || Infinity) < minLen) minLen = d.min_length || Infinity;
      if (item.product.is_always_raq) isAlwaysRaq = true;
    });
    return { products: matching, maxLength: maxLen || null, minLength: minLen === Infinity ? null : minLen, isAlwaysRaq: isAlwaysRaq };
  }

  // ---------- line state machine ----------
  function newLine() {
    return {
      id: 'l_' + Date.now() + '_' + Math.floor(Math.random() * 1000),
      step: 'pick_material',           // pick_material | pick_shape | pick_dimensions | qty_length | sku_qty_length | sourcing | freeform
      mode: 'catalog',                 // catalog | sourcing | freeform
      via: 'cascade',                  // cascade | sku | prefill
      material: null, shape: null, grade: null,
      axis_values: {}, dimensions: {},
      quantity: 1, length: '', width: '',
      products: [],
      maxLength: null, minLength: null,
      isAlwaysRaq: false, isSpecialtyFreight: false,
      sourcingDescription: '', customerUnsureFields: [],
      // Structured sourcing fields — free-text since we're outside the catalog.
      // material/shape/dimensions are required (validated in commitLine);
      // grade + sourcingDescription are optional.
      sourcingMaterial: '', sourcingShape: '', sourcingDimensions: '', sourcingGrade: '',
      // Freeform ("paste your list", v5) — one row of the customer's own
      // text, sent verbatim. No client-side parsing, ever.
      freeformText: '',
      sku: null,
      handle: null,
      // v2 picker state
      titlePicker: false,              // user switched to the full product list
      collision: null,                 // >1 product matched axes+grade — pick by title
      // Degraded prefill: ?product handle not in the catalog, pinned from
      // the live /products/<handle>.js payload instead.
      degraded: false,
      degradedSheet: false,
      pinned: null,                    // { product_id, title, sku, handle, product_type }
    };
  }

  function pickMaterial(matId) {
    var m = findMaterial(matId);
    if (!m) return convertToSourcing('material');
    var line = state.activeLine;
    line.material = { id: m.id, label: m.label };
    line.step = 'pick_shape';
    line.via = 'cascade';
    render();
  }

  function pickShape(shapeId) {
    var line = state.activeLine;
    var s = findShape(line.material.id, shapeId);
    if (!s) return convertToSourcing('shape');
    line.shape = shapeSnapshot(s);
    line.axis_values = {};
    line.products = [];
    line.titlePicker = false;
    line.collision = null;
    var g = defaultGrade(s);
    if (g) line.grade = { id: g.id, label: g.label };
    line.step = 'pick_dimensions';
    render();
  }

  function setAxis(axis, val) {
    if (val === '__sourcing__') return convertToSourcing(axis);
    var line = state.activeLine;
    if (!line) return;
    if (val === '' || val == null) delete line.axis_values[axis];
    else line.axis_values[axis] = parseFloat(val);
    if (lineIsV2(line)) {
      // Later axes only offer real combinations, so anything picked after
      // this one is stale — clear downstream picks and any resolution.
      var keys = line.shape.required_axes.map(function (ax) { return ax.key; });
      for (var j = keys.indexOf(axis) + 1; j < keys.length; j++) delete line.axis_values[keys[j]];
      line.products = [];
      line.collision = null;
      if (axesComplete(line.shape, line.axis_values)) {
        var grades = availableGradesV2(line.shape, line.axis_values);
        if (!grades.length) return convertToSourcing('dimensions');
        if (!line.grade || !grades.some(function (g) { return g.id === line.grade.id; })) {
          line.grade = { id: grades[0].id, label: grades[0].label };
        }
        // Single grade — nothing left to decide, resolve right away.
        // Multiple grades — render the grade select + Continue instead.
        if (grades.length === 1) return resolveAndAdvance(line);
      }
    }
    render();
  }

  function setGrade(gradeId) {
    var line = state.activeLine;
    var g = (line.shape.grades || []).filter(function (x) { return x.id === gradeId; })[0];
    if (g) line.grade = { id: g.id, label: g.label };
  }

  // v2: explicit grade pick is the last decision on the cascade.
  function pickGradeV2(val) {
    if (!val) return;
    if (val === '__sourcing__') return convertToSourcing('grade');
    var line = state.activeLine;
    if (!line) return;
    setGrade(val);
    line.collision = null;
    resolveAndAdvance(line);
  }

  // v2: axes + grade must pin EXACTLY ONE product. Zero matches falls to
  // sourcing (same as legacy); more than one is a catalog data collision
  // and the customer picks the exact product by its full title.
  function resolveAndAdvance(line) {
    if (!axesComplete(line.shape, line.axis_values)) return showError(t('errPickDims', 'Pick all dimensions first.'));
    clearError();
    var resolved = resolveSelection(line.shape, line.axis_values, line.grade && line.grade.id);
    if (!resolved.products.length) return convertToSourcing('dimensions');
    if (resolved.products.length > 1) {
      line.collision = resolved.products;
      render();
      return;
    }
    pinProduct(line, resolved.products[0]);
  }

  function pinProduct(line, entry) {
    line.grade = { id: entry.grade.id, label: entry.grade.label };
    // Product wins on shared keys; its axis_values carry EVERY required
    // axis (when derivable), so the payload dims come out complete.
    line.axis_values = Object.assign({}, line.axis_values, entry.product.axis_values || {});
    line.products = [entry];
    var d = entry.product.dimensions_available || {};
    line.maxLength = d.max_length || null;
    line.minLength = d.min_length || null;
    line.isAlwaysRaq = !!entry.product.is_always_raq;
    line.sku = entry.product.sm3_sku || null;
    line.handle = entry.product.handle || null;
    line.collision = null;
    line.step = 'qty_length';
    clearError();
    render();
  }

  function pickProductByIndex(val) {
    if (!val) return;
    if (val === '__sourcing__') return convertToSourcing('product');
    var line = state.activeLine;
    if (!line || !line.shape) return;
    var entry = titlePickerEntries(line)[parseInt(val, 10)];
    if (!entry) return;
    pinProduct(line, entry);
  }

  function allDimsSet(line) {
    var axes = (line.shape && line.shape.stocked_axes) || [];
    if (!axes.length) return true;
    return axes.every(function (a) { return line.axis_values[a] != null && !isNaN(line.axis_values[a]); });
  }

  function continueToQty() {
    var line = state.activeLine;
    if (!allDimsSet(line)) return showError(t('errPickDims', 'Pick all dimensions first.'));
    clearError();
    var resolved = resolveSelection(line.shape, line.axis_values);
    line.products = resolved.products;
    line.maxLength = resolved.maxLength;
    line.minLength = resolved.minLength;
    line.isAlwaysRaq = resolved.isAlwaysRaq;
    if (!resolved.products.length) return convertToSourcing('dimensions');
    line.step = 'qty_length';
    render();
  }

  function commitLine() {
    var line = state.activeLine;
    if (!line) return;
    if (line.mode === 'catalog') {
      if (!line.length || !String(line.length).trim()) return showError(t('errEnterLength', 'Enter a length.'));
      var lenN = parseInches(line.length);
      if (isNaN(lenN) || lenN <= 0) return showError(t('errLengthNumber', 'Length needs to be a number in inches.'));
      if (needsWidth(line)) {
        if (!line.width || !String(line.width).trim()) return showError(t('errEnterWidth', 'Enter a width.'));
        var widN = parseInches(line.width);
        if (isNaN(widN) || widN <= 0) return showError(t('errWidthNumber', 'Width needs to be a number in inches.'));
      }
      if (!line.quantity || line.quantity < 1) return showError(t('errQtyMin', 'Quantity must be at least 1.'));
      // v2 lines commit against ONE pinned product, with every required
      // axis accounted for (title-picked and axis_incomplete products carry
      // their identity in the pin itself).
      if (lineIsV2(line)) {
        if (!line.products || line.products.length !== 1) return showError(t('errPickProduct', 'Pick the exact product from the list.'));
        var pp = line.products[0].product;
        if (!pp.axis_incomplete && !isPickByTitleShape(line.shape)) {
          var reqAxes = line.shape.required_axes || [];
          for (var ai = 0; ai < reqAxes.length; ai++) {
            var avv = line.axis_values[reqAxes[ai].key];
            if (avv == null || isNaN(parseFloat(avv))) return showError(t('errPickDims', 'Pick all dimensions first.'));
          }
        }
      }
      if (line.minLength && lenN < line.minLength) {
        return showError(t('errMinLength', 'Shortest cut for this item is {min}". Enter a length of {min}" or more.', { min: line.minLength }));
      }
      if (line.maxLength && lenN > line.maxLength) line.isSpecialtyFreight = true;
    } else {
      if (!line.sourcingMaterial || !line.sourcingMaterial.trim()) return showError(t('errSourcingMat', 'Enter a material (e.g. Inconel 718, 7075-T6 aluminum).'));
      if (!line.sourcingShape || !line.sourcingShape.trim()) return showError(t('errSourcingShape', 'Enter a shape (e.g. Round bar, Plate, Tube).'));
      if (!line.sourcingDimensions || !line.sourcingDimensions.trim()) return showError(t('errSourcingDims', 'Enter dimensions (e.g. 4" OD × 0.25" wall).'));
      var srcLenN = parseInches(line.length);
      if (!line.length || isNaN(srcLenN) || srcLenN <= 0) return showError(t('errSourcingLength', 'Enter a length per piece (in inches).'));
      if (!line.quantity || line.quantity < 1) return showError(t('errQtyMin', 'Quantity must be at least 1.'));
    }
    clearError();
    line.step = 'done';
    state.lines.push(line);
    state.activeLine = null;
    render();
    // Scroll to "Items" section so the user sees their line land
    var c = DOM.committed;
    if (c) c.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function removeLine(id) {
    state.lines = state.lines.filter(function (l) { return l.id !== id; });
    if (state.step !== 'lines') state.step = 'lines';
    if (!state.lines.length && !state.activeLine) state.activeLine = newLine();
    render();
  }

  function editLine(id) {
    var idx = state.lines.findIndex(function (l) { return l.id === id; });
    if (idx === -1) return;
    var l = state.lines.splice(idx, 1)[0];
    l.step = l.mode === 'freeform' ? 'freeform'
      : l.mode === 'sourcing' ? 'sourcing'
      : (l.via === 'sku' ? 'sku_qty_length' : 'qty_length');
    state.activeLine = l;
    state.step = 'lines';
    render();
    DOM.builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function convertToSourcing(triggeredBy) {
    var line = state.activeLine;
    if (!line) line = state.activeLine = newLine();
    line.mode = 'sourcing';
    line.step = 'sourcing';
    if (triggeredBy && line.customerUnsureFields.indexOf(triggeredBy) === -1) {
      line.customerUnsureFields.push(triggeredBy);
    }
    render();
  }

  // ---------- SKU snap ----------
  function snapSku(rawSku) {
    var sku = (rawSku || '').trim().toUpperCase();
    if (!sku) return;
    if (state.catalogStatus !== 'ready') {
      showError(t('errCatalogLoading', 'Materials catalog is still loading. One sec.'));
      return;
    }
    var match = findBySku(sku);
    if (!match) {
      // Render a "not found" state in the SKU zone
      renderSkuNotFound(sku);
      return;
    }
    var line = state.activeLine || newLine();
    state.activeLine = line;
    line.material = { id: match.material.id, label: match.material.label };
    line.shape = shapeSnapshot(match.shape);
    line.grade = { id: match.grade.id, label: match.grade.label };
    line.axis_values = Object.assign({}, match.product.axis_values || {});
    line.products = [{ grade: match.grade, product: match.product }];
    var d = match.product.dimensions_available || {};
    line.maxLength = d.max_length || null;
    line.minLength = d.min_length || null;
    line.isAlwaysRaq = !!match.product.is_always_raq;
    line.sku = match.product.sm3_sku;
    line.handle = match.product.handle;
    line.via = 'sku';
    line.step = 'sku_qty_length';
    clearError();
    render();
  }

  // ---------- prefill via URL params ----------
  function parsePrefill() {
    var p = new URLSearchParams(window.location.search);
    var variantId = p.get('variant');
    var productHandle = p.get('product');
    var sku = p.get('sku');
    var qty = parseInt(p.get('qty'), 10);
    // Optional cut size + note carried from a PDP over-max request.
    var width = p.get('width');
    var length = p.get('length');
    var note = p.get('note');
    if (!variantId && !productHandle && !sku) return null;
    return { variantId: variantId, productHandle: productHandle, sku: sku, qty: isNaN(qty) ? 1 : qty, width: width, length: length, note: note };
  }

  function applyPrefill() {
    var p = state.prefill;
    if (!p || !state.catalog) return;
    // Carry a PDP note (e.g. an over-max size request) into the quote even if
    // the product can't be resolved in the catalog below. Don't clobber typed notes.
    if (p.note && !state.submission.notes) state.submission.notes = p.note;
    renderPrefillStamp(p.note);
    var match = null;
    if (p.sku) match = findBySku(p.sku);
    if (!match && p.productHandle) match = findByHandle(p.productHandle);
    if (!match) {
      // Not in the catalog (new product, catalog lag, fixed-length item).
      // Pin the line from the live product JSON instead of silently
      // dropping the customer onto a blank form.
      if (p.productHandle) applyPrefillFallback(p);
      else console.warn('[rfq] prefill not found in catalog', p);
      return;
    }
    var line = newLine();
    state.activeLine = line;
    line.material = { id: match.material.id, label: match.material.label };
    line.shape = shapeSnapshot(match.shape);
    line.grade = { id: match.grade.id, label: match.grade.label };
    line.axis_values = Object.assign({}, match.product.axis_values || {});
    line.products = [{ grade: match.grade, product: match.product }];
    var d = match.product.dimensions_available || {};
    line.maxLength = d.max_length || null;
    line.minLength = d.min_length || null;
    line.isAlwaysRaq = !!match.product.is_always_raq;
    line.sku = match.product.sm3_sku;
    line.handle = match.product.handle;
    line.quantity = p.qty || 1;
    // Carry the requested cut size (e.g. a PDP over-max entry) into the line so
    // staff see exactly what was asked for. width renders for sheets only.
    var pw = parseFloat(p.width);
    var pl = parseFloat(p.length);
    if (!isNaN(pw) && pw > 0) line.width = String(pw);
    if (!isNaN(pl) && pl > 0) line.length = String(pl);
    line.via = 'prefill';
    line.step = 'sku_qty_length';

    // Show prefill banner
    if (DOM.prefill && DOM.prefillText) {
      DOM.prefill.hidden = false;
      var sizeNote = (line.width || line.length)
        ? ' · ' + t('prefillRequested', 'requested') + ' ' + (line.width || '?') + '" × ' + (line.length || '?') + '"'
        : '';
      DOM.prefillText.textContent = match.material.label + ' ' + match.shape.label + ' · ' + match.grade.label + (line.axis_values && Object.keys(line.axis_values).length ? ' · ' + Object.keys(line.axis_values).map(function (a) { return fmtDim(line.axis_values[a]); }).join(' × ') : '') + sizeNote;
    }
  }

  // The PDP's low-stock notice sends the customer here with a "Sourcing
  // request: ..." note when they want more than the shelf holds. Say the $250
  // rule again before they type, so the quote never surprises them (2026-09-07).
  function renderPrefillStamp(note) {
    if (!DOM.prefillStamp) return;
    var isSourcing = /^(Sourcing request|Demande d.approvisionnement)/i.test(String(note || '').trim());
    if (!isSourcing) { DOM.prefillStamp.hidden = true; DOM.prefillStamp.innerHTML = ''; return; }
    var shelf = String(note).replace(/^[^.]*\.\s*/, '').replace(/\s*(10 to 21 days|10 a 21 jours)[^]*$/i, '').trim();
    DOM.prefillStamp.innerHTML = '<div class="rfq-sourcing-stamp">' +
      '<p class="rfq-sourcing-stamp__title">' + esc(t('lowStockTitle', 'Low stock on this item')) + '</p>' +
      '<ul class="rfq-sourcing-stamp__list">' +
        (shelf ? '<li>' + esc(shelf) + '</li>' : '') +
        '<li>' + t('lowStockRule', 'We bring more in for orders of <strong>$250 or more</strong>, 10 to 21 days. The mill makes us buy far more than your job needs and the rest sits on our racks for months, so the order has to be worth the shelf it takes.') + '</li>' +
        '<li>' + t('lowStockUnder', 'Under $250? The shelf changes with every delivery. The stock alert on the product page emails you when there\'s more.') + '</li>' +
      '</ul></div>';
    DOM.prefillStamp.hidden = false;
  }

  // Degraded-but-structured prefill: the handle isn't in the catalog, so
  // pull the live product JSON and pin the line from it. Staff still get
  // the exact product (id, title, first-variant SKU) on the quote.
  function applyPrefillFallback(p) {
    fetch('/products/' + encodeURIComponent(p.productHandle) + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (prod) {
        if (!prod || !prod.id) throw new Error('empty product');
        var line = newLine();
        line.degraded = true;
        line.via = 'prefill';
        line.pinned = {
          product_id: 'gid://shopify/Product/' + prod.id,
          title: prod.title || p.productHandle,
          sku: (prod.variants && prod.variants[0] && prod.variants[0].sku) || null,
          handle: prod.handle || p.productHandle,
          product_type: prod.type || '',
        };
        line.degradedSheet = /sheet|plate/i.test(prod.type || '');
        line.quantity = p.qty || 1;
        var pw = parseFloat(p.width);
        var pl = parseFloat(p.length);
        if (!isNaN(pw) && pw > 0) line.width = String(pw);
        if (!isNaN(pl) && pl > 0) line.length = String(pl);
        line.step = 'sku_qty_length';
        state.activeLine = line;
        if (DOM.prefill && DOM.prefillText) {
          DOM.prefill.hidden = false;
          DOM.prefillText.textContent = line.pinned.title;
        }
        render();
      })
      .catch(function (err) {
        console.warn('[rfq] prefill product fetch failed', p, err);
      });
  }

  // ---------- customer hydrate ----------
  function hydrateCustomer() {
    var d = ROOT.dataset;
    if (d.customerEmail) {
      state.customer.email = d.customerEmail;
      state.customer.first_name = d.customerFirst || '';
      state.customer.last_name = d.customerLast || '';
      state.customer.phone = d.customerPhone || '';
      state.user = { email: d.customerEmail, name: ((d.customerFirst || '') + ' ' + (d.customerLast || '')).trim() };
    }
  }

  function continueToCustomer() {
    if (!state.lines.length) return showError(t('errAddBeforeContinue', 'Add at least one item before continuing.'));
    clearError();
    state.step = 'customer';
    render();
    if (DOM.customer) DOM.customer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function continueToSubmission() {
    if (!customerValid()) return showError(t('errFillContact', 'Fill in name, email, and province.'));
    clearError();
    state.step = 'submission';
    render();
    if (DOM.submission) DOM.submission.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function customerValid() {
    var c = state.customer;
    return c.first_name && c.last_name && c.email && c.email.indexOf('@') > 0 && c.ship_to_province;
  }

  // ---------- render ----------
  function render() {
    renderHero();
    renderBuilder();
    renderCustomer();
    renderSubmission();
    renderSubmitState();
    renderTicket();
  }

  function renderHero() {
    var n = state.lines.length;
    var label = tn('itemOne', 'itemOther', '{n} item', '{n} items', n);
    if (DOM.tallyCounts) {
      DOM.tallyCounts.forEach(function (el) { el.textContent = label; });
    }
    if (DOM.tallySub) {
      DOM.tallySub.textContent = state.lines.length
        ? t('pricingHeld', 'Pricing held 14 days from quote date.')
        : t('addToStart', 'Add an item below to get started.');
    }
  }

  function renderBuilder() {
    if (!DOM.builder || !DOM.skuZone || !DOM.build) return;
    var line = state.activeLine || (state.activeLine = newLine());

    if (line.via === 'sku' || line.via === 'prefill') {
      renderSkuMatched(line);
      renderQtyLength(line);
    } else if (line.step === 'sourcing') {
      renderSkuEmpty();
      renderSourcing(line);
    } else if (line.step === 'freeform') {
      renderSkuEmpty();
      renderFreeform(line);
    } else if (line.step === 'qty_length') {
      // Cascade complete — show the same green resolved-product card so
      // the customer can see WHAT they picked while sizing it.
      renderSkuMatched(line);
      renderQtyLength(line);
    } else {
      // pick_material | pick_shape | pick_dimensions
      renderSkuEmpty();
      renderCascade(line);
    }
  }

  // ---------- SKU zone (empty / matched / not found) ----------
  function renderSkuEmpty() {
    DOM.skuZone.innerHTML =
      '<div class="rfq-sku-row">' +
        '<div class="rfq-field rfq-field--mono">' +
          '<input type="text" data-rfq-sku-input id="rfq-sku-input" placeholder="' + esc(t('skuPlaceholder', 'e.g. 2338')) + '" maxlength="8" autocomplete="off">' +
          '<label class="rfq-field__label" for="rfq-sku-input">' + esc(t('skuLabel', 'Product SKU')) + '</label>' +
        '</div>' +
        '<button type="button" class="rfq-btn-find" data-rfq-sku-snap>' + esc(t('skuFind', 'Find')) + '</button>' +
      '</div>';
    bindSkuZone();
    bindFieldFocus(DOM.skuZone);
  }

  function renderSkuMatched(line) {
    // The pinned product's FULL title fronts the card; the composite
    // material · shape · grade line is the fallback when nothing is pinned
    // (legacy multi-match under a stale cached catalog).
    var pinned = (line.products && line.products.length === 1 && line.products[0]) ? line.products[0].product : null;
    var fullTitle = (line.degraded && line.pinned && line.pinned.title) || (pinned && pinned.title) || '';
    var specKeys = Object.keys(line.axis_values || {});
    if (lineIsV2(line) && line.shape.required_axes.length) {
      specKeys = line.shape.required_axes.map(function (ax) { return ax.key; })
        .filter(function (k) { return line.axis_values[k] != null; });
    }
    var spec = specKeys.map(function (a) { return prettyAxisForLine(line, a) + ' ' + fmtDim(line.axis_values[a]); }).join(' · ');
    var skuLabel = line.via === 'prefill' ? t('fromProductPage', 'From product page')
      : line.via === 'sku' ? (t('skuPrefix', 'SKU') + ' ' + esc(line.sku || ''))
      : t('builtFromPicker', 'Built from picker');
    var titleHtml = fullTitle ? esc(fullTitle)
      : (line.material && line.shape && line.grade)
        ? esc(line.material.label) + ' ' + esc(line.shape.label) + ' · ' + esc(line.grade.label)
        : esc(t('listItem', 'List item'));
    DOM.skuZone.innerHTML =
      '<div class="rfq-resolved">' +
        '<svg class="rfq-resolved__check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12l4 4 10-10"/></svg>' +
        '<div class="rfq-resolved__thumb">' + shapeIcon(line.shape && line.shape.id) + '</div>' +
        '<div class="rfq-resolved__body">' +
          '<p class="rfq-resolved__title">' + titleHtml + '</p>' +
          '<div class="rfq-resolved__meta">' +
            '<span class="rfq-resolved__sku">' + esc(skuLabel) + '</span>' +
            (spec ? ' · ' + esc(spec) : '') +
            (line.isAlwaysRaq ? ' · ' + esc(t('quoteOnlyTag', 'Quote-only product')) : '') +
          '</div>' +
        '</div>' +
        '<button type="button" class="rfq-resolved__change" data-rfq-action="reset-line">' + esc(t('resolvedChange', 'Change')) + '</button>' +
      '</div>';
    var btn = DOM.skuZone.querySelector('[data-rfq-action="reset-line"]');
    if (btn) btn.addEventListener('click', function () {
      state.activeLine = null;
      if (DOM.prefill) DOM.prefill.hidden = true; if (DOM.prefillStamp) DOM.prefillStamp.hidden = true;
      render();
    });
  }

  function renderSkuNotFound(sku) {
    DOM.skuZone.innerHTML =
      '<div class="rfq-resolved rfq-resolved--error">' +
        '<svg class="rfq-resolved__check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h0"/></svg>' +
        '<div class="rfq-resolved__body">' +
          '<p class="rfq-resolved__title">' + esc(t('skuNotFound', 'SKU {sku} not found', { sku: sku })) + '</p>' +
          '<div class="rfq-resolved__meta">' + esc(t('skuNotFoundHint', 'Double-check the SKU, or use the picker below.')) + '</div>' +
        '</div>' +
        '<button type="button" class="rfq-resolved__change" data-rfq-action="reset-sku">' + esc(t('tryAgain', 'Try again')) + '</button>' +
      '</div>';
    var btn = DOM.skuZone.querySelector('[data-rfq-action="reset-sku"]');
    if (btn) btn.addEventListener('click', renderSkuEmpty);
  }

  function bindSkuZone() {
    var input = DOM.skuZone.querySelector('[data-rfq-sku-input]');
    var btn = DOM.skuZone.querySelector('[data-rfq-sku-snap]');
    if (!input || !btn) return;
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); snapSku(input.value); } });
    btn.addEventListener('click', function () { snapSku(input.value); });
  }

  // ---------- cascade picker (Material / Shape / Size&Grade) ----------
  function renderCascade(line) {
    if (state.catalogStatus !== 'ready') {
      DOM.build.innerHTML = '<div class="rfq-loading">' + esc(t('loadingCatalog', 'Loading materials catalog…')) + '</div>';
      return;
    }
    var mats = getMaterials();
    // v2 shapes get one select per required axis; anything else (old cached
    // catalog, or a v2 catalog whose shape lacks required_axes) keeps the
    // combined Size & Grade combo.
    var shapeV2 = line.shape ? lineIsV2(line) : catalogV2();
    var html = '<div class="rfq-or"><span>' + esc(t('orBuildManually', 'or build manually')) + '</span></div>';

    // Material
    var matField = '<div class="rfq-field">';
    matField +=   '<select data-rfq-action="pick-material">';
    matField +=     '<option value="">' + esc(t('pickMaterial', 'Pick material…')) + '</option>';
    mats.forEach(function (m) {
      var sel = (line.material && line.material.id === m.id) ? ' selected' : '';
      matField += '<option value="' + esc(m.id) + '"' + sel + '>' + esc(m.label) + '</option>';
    });
    matField +=   '<option value="" disabled>──────────</option>';
    matField +=   '<option value="__sourcing__">' + esc(t('sourcingMaterialOpt', "Don't see your material? (sourcing request)")) + '</option>';
    matField +=   '</select>';
    matField +=   '<label class="rfq-field__label">' + esc(t('materialLabel', 'Material')) + ' <span class="rfq-field__req">*</span></label>';
    matField += '</div>';

    // Shape
    var shapeField = '<div class="rfq-field">';
    if (line.material) {
      var mat = findMaterial(line.material.id);
      var shapes = mat ? (mat.shapes || []) : [];
      shapeField += '<select data-rfq-action="pick-shape">';
      shapeField +=   '<option value="">' + esc(t('pickShape', 'Pick shape…')) + '</option>';
      shapes.forEach(function (s) {
        var sel = (line.shape && line.shape.id === s.id) ? ' selected' : '';
        shapeField += '<option value="' + esc(s.id) + '"' + sel + '>' + esc(s.label) + '</option>';
      });
      shapeField += '<option value="" disabled>──────────</option>';
      shapeField += '<option value="__sourcing__">' + esc(t('sourcingShapeOpt', "Don't see your shape? (sourcing request)")) + '</option>';
      shapeField += '</select>';
    } else {
      shapeField += '<select disabled><option>' + esc(t('pickMaterialFirst', 'Pick material first')) + '</option></select>';
    }
    shapeField +=   '<label class="rfq-field__label">' + esc(t('shapeLabel', 'Shape')) + ' <span class="rfq-field__req">*</span></label>';
    shapeField += '</div>';

    if (!shapeV2) {
      // Legacy layout: Material / Shape / Size & Grade combo.
      html += '<div class="rfq-fieldgrid rfq-fieldgrid--3">' + matField + shapeField;
      html += '<div class="rfq-field">';
      if (line.shape) {
        html += renderDimSelect(line);
      } else {
        html += '<select disabled><option>' + esc(t('pickShapeFirst', 'Pick shape first')) + '</option></select>';
      }
      html +=   '<label class="rfq-field__label">' + t('sizeGradeLabel', 'Size &amp; Grade') + ' <span class="rfq-field__req">*</span></label>';
      html += '</div>';
      html += '</div>'; // /fieldgrid
    } else {
      // v2 layout: Material / Shape, then the per-axis row once a shape is picked.
      html += '<div class="rfq-fieldgrid rfq-fieldgrid--2">' + matField + shapeField + '</div>';
      if (line.shape) html += renderAxisZone(line);
    }

    // Paste-your-list fast lane (v5): for the buyer holding a cut list who
    // doesn't want to walk the picker item by item. Converts straight into
    // verbatim lines our team prices by hand — friction beats data purity
    // here, because an un-submitted picker session is worth nothing.
    html += '<button type="button" class="rfq-sourcing-toggle rfq-sourcing-toggle--paste" data-rfq-action="goto-freeform">' +
      '<span class="rfq-sourcing-toggle__icon" aria-hidden="true">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 4h6M9 4a2 2 0 0 0-2 2v0H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-1M9 4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M8 11h8M8 15h5"/></svg>' +
      '</span>' +
      '<span class="rfq-sourcing-toggle__body">' +
        '<strong>' + esc(t('pasteTitle', 'In a hurry? Paste your list')) + '</strong>' +
        esc(t('pasteSub', "Drop in your cut list or describe the job and we'll sort out the details and come back with pricing.")) +
      '</span>' +
      '<span class="rfq-sourcing-toggle__arrow" aria-hidden="true">→</span>' +
    '</button>';

    // Sourcing escape hatch
    html += '<button type="button" class="rfq-sourcing-toggle" data-rfq-action="goto-sourcing">' +
      '<span class="rfq-sourcing-toggle__icon" aria-hidden="true">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>' +
      '</span>' +
      '<span class="rfq-sourcing-toggle__body">' +
        '<strong>' + esc(t('sourcingTitle', 'Metal sourcing')) + '</strong>' +
        esc(t('sourcingSub', "Not in our regular inventory? Tell us the details. We order it in for you on requests of $250 or more.")) +
      '</span>' +
      '<span class="rfq-sourcing-toggle__arrow" aria-hidden="true">→</span>' +
    '</button>';

    DOM.build.innerHTML = html;
    bindBuildActions();
    bindFieldFocus(DOM.build);
  }

  // ---------- freeform "paste your list" (v5) ----------
  function renderFreeform(line) {
    var html = '<div class="rfq-sourcing-stamp rfq-sourcing-stamp--paste">' +
      '<p class="rfq-sourcing-stamp__title">' + esc(t('pasteStampTitle', 'Paste Your List')) + '</p>' +
      '<ul class="rfq-sourcing-stamp__list">' +
        '<li>' + esc(t('pasteStamp1', 'One item per line: material, size, length, quantity, in your own words.')) + '</li>' +
        '<li>' + esc(t('pasteStamp2', 'Photos or drawings? Attach them in the last step below.')) + '</li>' +
        '<li>' + esc(t('pasteStamp3', 'Our team reads every line and replies with itemized pricing.')) + '</li>' +
      '</ul></div>';

    html += '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
      '<div class="rfq-field">' +
        '<textarea rows="7" data-rfq-action="set-freeform" id="rfq-freeform-text" placeholder="' +
          esc(t('freeformPlaceholder', '3 pcs of 2x2x1/4 aluminum angle, 96" long\n1 sheet 304 stainless, 16ga, 24" x 48"\n10 ft of 1" round bar, mild steel')) +
        '">' + esc(line.freeformText || '') + '</textarea>' +
        '<label class="rfq-field__label" for="rfq-freeform-text">' + esc(t('freeformLabel', 'Your list')) + ' <span class="rfq-field__req">*</span></label>' +
      '</div>' +
    '</div>';

    html += '<div class="rfq-actions">' +
      '<button class="rfq-btn rfq-btn--primary" type="button" data-rfq-action="commit-freeform">' + esc(t('addListBtn', 'Add List to Quote')) + '</button>' +
      '<button class="rfq-btn rfq-btn--text" type="button" data-rfq-action="cancel-sourcing">' + esc(t('cancel', 'Cancel')) + '</button>' +
    '</div>';

    DOM.build.innerHTML = html;
    bindBuildActions();
    bindFieldFocus(DOM.build);
  }

  // Each non-empty pasted row becomes its own line item, verbatim. Staff
  // price per row, mirroring how the customer wrote the list. NO parsing —
  // a wrong guess at "3x" vs "3 inch" would cost trust; humans read it fine.
  var FREEFORM_MAX_ROWS = 20;
  function commitFreeformList() {
    var line = state.activeLine;
    if (!line) return;
    var rows = String(line.freeformText || '')
      .split(/\r?\n/)
      .map(function (r) { return r.trim(); })
      .filter(Boolean);
    if (!rows.length) return showError(t('errFreeformEmpty', 'Type or paste at least one item.'));
    if (rows.length > FREEFORM_MAX_ROWS) {
      return showError(t('errFreeformLong', 'That is a long list. Keep it under {max} lines, or split it into two requests.', { max: FREEFORM_MAX_ROWS }));
    }
    clearError();
    rows.forEach(function (r) {
      var l = newLine();
      l.mode = 'freeform';
      l.step = 'done';
      l.freeformText = r;
      l.quantity = 1;
      state.lines.push(l);
    });
    state.activeLine = null;
    render();
    var c = DOM.committed;
    if (c) c.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ---------- per-axis pickers (catalog format v2) ----------
  // One select per required axis (ordered), each filtered to real
  // combinations, then the grade select. Tiny/irregular shapes and
  // axis_incomplete products go through a full-title product picker.
  function renderAxisZone(line) {
    var shape = line.shape;
    var axes = shape.required_axes || [];
    if (isTitleModeLine(line)) {
      var html = renderTitlePicker(line, titlePickerEntries(line));
      if (line.titlePicker && !isPickByTitleShape(shape)) {
        html += '<button type="button" class="rfq-btn rfq-btn--text" data-rfq-action="back-to-axes" style="padding-left:0;">' + esc(t('backToSizes', 'Back to the size pickers')) + '</button>';
      }
      return html;
    }
    var allSet = axesComplete(shape, line.axis_values);
    var html = '<div class="rfq-fieldgrid rfq-fieldgrid--axes" style="margin-top:12px;">';
    axes.forEach(function (ax, i) {
      var opts = axisOptionsV2(shape, line.axis_values, i);
      html += '<div class="rfq-field">';
      if (opts) {
        var cur = line.axis_values[ax.key];
        html += '<select data-rfq-action="pick-axis" data-rfq-axis="' + esc(ax.key) + '">';
        html += '<option value="">' + esc(axisLabelOf(ax)) + '…</option>';
        opts.forEach(function (v) {
          var sel = (cur != null && parseFloat(cur) === v) ? ' selected' : '';
          html += '<option value="' + v + '"' + sel + '>' + esc(fmtDim(v)) + '</option>';
        });
        html += '<option value="" disabled>──────────</option>';
        html += '<option value="__sourcing__">' + esc(t('sourcingSizeOpt', "Don't see your size? (sourcing request)")) + '</option>';
        html += '</select>';
      } else {
        var prev = axes[i - 1];
        html += '<select disabled><option>' + esc(t('pickAxisFirst', 'Pick {label} first', { label: prev ? axisLabelOf(prev) : t('shapeLabel', 'Shape') })) + '</option></select>';
      }
      html += '<label class="rfq-field__label">' + esc(axisLabelOf(ax)) + ' <span class="rfq-field__req">*</span></label>';
      html += '</div>';
    });
    // Grade — default preselected (first grade with a matching product),
    // same default-grade behavior as the legacy combo.
    html += '<div class="rfq-field">';
    if (allSet) {
      var grades = availableGradesV2(shape, line.axis_values);
      html += '<select data-rfq-action="pick-grade">';
      grades.forEach(function (g) {
        var sel = (line.grade && line.grade.id === g.id) ? ' selected' : '';
        html += '<option value="' + esc(g.id) + '"' + sel + '>' + esc(g.label) + '</option>';
      });
      html += '<option value="" disabled>──────────</option>';
      html += '<option value="__sourcing__">' + esc(t('sourcingGradeOpt', "Don't see your grade? (sourcing request)")) + '</option>';
      html += '</select>';
    } else {
      html += '<select disabled><option>' + esc(t('pickSizesFirst', 'Pick the sizes first')) + '</option></select>';
    }
    html += '<label class="rfq-field__label">' + esc(t('gradeLabel', 'Grade')) + ' <span class="rfq-field__req">*</span></label>';
    html += '</div>';
    html += '</div>'; // /axes grid

    if (line.collision && line.collision.length) {
      html += '<div class="rfq-help" style="margin-top:10px;">' + esc(t('collisionHelp', 'A few products match that size. Pick the exact one so we quote the right product.')) + '</div>';
      html += renderTitlePicker(line, line.collision);
    } else if (allSet) {
      html += '<div class="rfq-actions">' +
        '<button class="rfq-btn rfq-btn--primary" type="button" data-rfq-action="axes-continue">' + esc(t('continueBtn', 'Continue')) + '</button>' +
      '</div>';
    }
    if (shapeHasIncomplete(shape) && !line.collision) {
      html += '<button type="button" class="rfq-btn rfq-btn--text" data-rfq-action="goto-title-picker" style="padding-left:0;">' + esc(t('byTitleToggle', "Can't find your size? Pick from the full product list")) + '</button>';
    }
    return html;
  }

  // Product picker: options are FULL product titles, value = index into
  // titlePickerEntries(line) (recomputed identically in the handler).
  function renderTitlePicker(line, entries) {
    var html = '<div class="rfq-field" style="margin-top:12px;">';
    html += '<select data-rfq-action="pick-product">';
    html += '<option value="">' + esc(t('pickProduct', 'Pick your product…')) + '</option>';
    entries.forEach(function (entry, i) {
      html += '<option value="' + i + '">' + esc(entry.product.title || entry.product.handle || '') + '</option>';
    });
    html += '<option value="" disabled>──────────</option>';
    html += '<option value="__sourcing__">' + esc(t('sourcingSizeOpt', "Don't see your size? (sourcing request)")) + '</option>';
    html += '</select>';
    html += '<label class="rfq-field__label">' + esc(t('productLabel', 'Product')) + ' <span class="rfq-field__req">*</span></label>';
    html += '</div>';
    return html;
  }

  // Combined "Size & Grade" select (legacy catalog path). Each option
  // encodes axis values + grade.
  function renderDimSelect(line) {
    var html = '<select data-rfq-action="pick-combo">';
    html += '<option value="">' + t('pickSizeGrade', 'Pick size &amp; grade…') + '</option>';
    var axes = (line.shape && line.shape.stocked_axes) || [];
    var grades = (line.shape && line.shape.grades) || [];
    grades.forEach(function (g) {
      (g.products || []).forEach(function (p) {
        var av = p.axis_values || {};
        var avLabel = axes.map(function (a) { return fmtDim(av[a]); }).filter(Boolean).join(' × ');
        var label = (avLabel || t('specify', 'Specify')) + (g.label && g.label !== '—' ? '  ·  ' + g.label : '');
        var encoded = JSON.stringify({ grade: g.id, av: av });
        var sel = (line.grade && line.grade.id === g.id && axes.every(function (a) { return parseFloat(line.axis_values[a]) === parseFloat(av[a]); })) ? ' selected' : '';
        html += '<option value="' + esc(encoded) + '"' + sel + '>' + esc(label) + '</option>';
      });
    });
    html += '<option value="" disabled>──────────</option>';
    html += '<option value="__sourcing__">' + esc(t('sourcingSizeOpt', "Don't see your size? (sourcing request)")) + '</option>';
    html += '</select>';
    return html;
  }

  function pickCombo(value) {
    if (!value) return;
    if (value === '__sourcing__') return convertToSourcing('dimensions');
    var line = state.activeLine;
    var parsed;
    try { parsed = JSON.parse(value); } catch (e) { return; }
    setGrade(parsed.grade);
    Object.keys(parsed.av).forEach(function (a) { line.axis_values[a] = parseFloat(parsed.av[a]); });
    var resolved = resolveSelection(line.shape, line.axis_values);
    line.products = resolved.products;
    line.maxLength = resolved.maxLength;
    line.minLength = resolved.minLength;
    line.isAlwaysRaq = resolved.isAlwaysRaq;
    if (!resolved.products.length) return convertToSourcing('dimensions');
    line.step = 'qty_length';
    render();
  }

  // Unit word for the qty descriptor, pluralized (sheet/sheets, length/lengths).
  // FR pluralizes the noun differently, so each form is its own i18n key.
  function unitWord(isSheet, qty) {
    if (isSheet) {
      return qty === 1 ? t('sheetUnit', 'sheet') : t('sheetUnitPlural', 'sheets');
    }
    return qty === 1 ? t('lengthUnit', 'length') : t('lengthUnitPlural', 'lengths');
  }

  // ---------- length & quantity ----------
  // Sheets and plate quote as width × length pieces; everything else is a
  // cut length only. Degraded prefill lines carry their own flag.
  function needsWidth(line) {
    if (!line) return false;
    if (line.degradedSheet) return true;
    return !!(line.shape && (line.shape.id === 'sheet' || line.shape.id === 'plate'));
  }

  function renderQtyLength(line) {
    var isSheet = line.shape && line.shape.id === 'sheet';
    var needsW = needsWidth(line);
    var maxLen = line.maxLength;
    var qty = line.quantity || 1;
    var unit = unitWord(isSheet, qty);
    var qtyDesc = line.length
      ? '<strong>' + qty + '</strong> &times; <strong>' + esc(line.length) + '"</strong> ' + unit
      : '<strong>' + qty + '</strong> ' + unit;

    // Length column: input + (slider + ticks if catalog max known).
    // Slider/ticks live INSIDE the column so they share its width.
    var lengthInner =
      '<div class="rfq-field">' +
        '<input type="text" data-rfq-action="set-length" inputmode="decimal" placeholder="36" value="' + esc(line.length) + '" id="rfq-cut-length">' +
        '<label class="rfq-field__label" for="rfq-cut-length">' + esc(isSheet ? t('lengthLabel', 'Length') : t('cutLengthLabel', 'Cut length')) + ' <span class="rfq-field__req">*</span></label>' +
        '<span class="rfq-field__suffix">' + esc(t('unitIn', 'in')) + '</span>' +
      '</div>';
    if (maxLen && !needsW) {
      lengthInner +=
        '<div class="rfq-slider"><div class="rfq-slider__fill" data-rfq-gauge style="width:0%;"></div></div>' +
        '<div class="rfq-slider__labels">' +
          '<span>0"</span>' +
          '<span>' + Math.round(maxLen * 0.25) + '"</span>' +
          '<span>' + Math.round(maxLen * 0.5) + '"</span>' +
          '<span>' + Math.round(maxLen * 0.75) + '"</span>' +
          '<span>' + maxLen + '"</span>' +
        '</div>';
    }
    var lengthCol = '<div class="rfq-length-stack">' + lengthInner + '</div>';

    // Qty column: stepper above, descriptor below — column width matches length column.
    var qtyBlock =
      '<div class="rfq-qty-block">' +
        '<span class="rfq-qty-block__label">' + esc(t('qtyLabel', 'Quantity')) + ' <span class="rfq-field__req">*</span></span>' +
        '<div class="rfq-qty-wrap">' +
          '<button class="rfq-qty-btn" type="button" data-rfq-action="qty-dec" aria-label="' + esc(t('decreaseAria', 'Decrease')) + '">−</button>' +
          '<input class="rfq-qty-field" type="number" min="1" data-rfq-action="set-qty" value="' + qty + '" aria-label="' + esc(t('qtyAria', 'Quantity')) + '">' +
          '<button class="rfq-qty-btn" type="button" data-rfq-action="qty-inc" aria-label="' + esc(t('increaseAria', 'Increase')) + '">+</button>' +
        '</div>' +
        '<div class="rfq-qty-desc">' + qtyDesc + '</div>' +
      '</div>';

    var html = '';
    if (needsW) {
      // Sheets and plate: Width / Length / Qty (slider not relevant here).
      var widthCol =
        '<div class="rfq-field">' +
          '<input type="text" data-rfq-action="set-width" inputmode="decimal" placeholder="48" value="' + esc(line.width) + '" id="rfq-cut-width">' +
          '<label class="rfq-field__label" for="rfq-cut-width">' + esc(t('widthLabel', 'Width')) + ' <span class="rfq-field__req">*</span></label>' +
          '<span class="rfq-field__suffix">' + esc(t('unitIn', 'in')) + '</span>' +
        '</div>';
      html += '<div class="rfq-fieldgrid rfq-fieldgrid--3">' + widthCol + lengthCol + qtyBlock + '</div>';
    } else {
      html += '<div class="rfq-fieldgrid rfq-fieldgrid--2">' + lengthCol + qtyBlock + '</div>';
    }

    // Single full-width helper line below the row.
    if (maxLen) {
      html += '<div class="rfq-help">' + t('helpIncrements', 'Quarter-inch increments. <strong>Min {min}"</strong>, <strong>Max {max}"</strong>. Need longer? Add it to your notes below.', { min: (line.minLength || 1), max: maxLen }) + '</div>';
    }

    if (line.isAlwaysRaq) {
      html += '<div class="rfq-callout rfq-callout--info">' + t('quoteOnlyCallout', "<strong>Quote-only product:</strong> this item isn't sold online. We'll quote it custom for you.") + '</div>';
    }

    html += '<div class="rfq-actions">' +
      '<button class="rfq-btn rfq-btn--primary" type="button" data-rfq-action="commit-line">' + esc(t('addToQuote', 'Add to Quote')) + '</button>' +
      '<button class="rfq-btn rfq-btn--text" type="button" data-rfq-action="reset-line">' + esc(t('reset', 'Reset')) + '</button>' +
    '</div>';

    DOM.build.innerHTML = html;
    bindBuildActions();
    bindFieldFocus(DOM.build);
    updateLengthGauge(line);
  }

  function updateLengthGauge(line) {
    var bar = DOM.build && DOM.build.querySelector('[data-rfq-gauge]');
    if (!bar || !line.maxLength) return;
    var v = parseInches(line.length);
    if (isNaN(v) || v <= 0) { bar.style.width = '0%'; bar.className = 'rfq-slider__fill'; return; }
    var pct = Math.min(100, (v / line.maxLength) * 100);
    bar.style.width = pct + '%';
    bar.className = 'rfq-slider__fill' + (v > line.maxLength ? ' rfq-slider__fill--over' : '');
  }

  function updateQtyDesc(line) {
    var el = DOM.build && DOM.build.querySelector('.rfq-qty-desc');
    if (!el) return;
    var isSheet = line.shape && line.shape.id === 'sheet';
    var qty = line.quantity || 1;
    var unit = unitWord(isSheet, qty);
    el.innerHTML = line.length
      ? '<strong>' + qty + '</strong> &times; <strong>' + esc(line.length) + '"</strong> ' + unit
      : '<strong>' + qty + '</strong> ' + unit;
  }

  // ---------- sourcing ----------
  function renderSourcing(line) {
    var html = '<div class="rfq-sourcing-stamp">' +
      '<p class="rfq-sourcing-stamp__title">' + esc(t('sourcingStampTitle', 'Sourcing Request')) + '</p>' +
      '<ul class="rfq-sourcing-stamp__list">' +
        '<li>' + t('sourcingStamp1', 'This item is <strong>not part of our regular inventory</strong>.') + '</li>' +
        '<li>' + t('sourcingStamp2', 'We order it in for you on requests of <strong>$250 or more</strong>. Under that we can\'t: the mill makes us buy far more than your job needs, and the rest sits on our racks for months.') + '</li>' +
        '<li>' + t('sourcingStamp3', 'Sourcing typically takes <strong>2-21 days</strong>.') + '</li>' +
      '</ul></div>';

    html += '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
      '<div class="rfq-field">' +
        '<input type="text" data-rfq-action="set-sourcing-material" value="' + esc(line.sourcingMaterial || '') + '" placeholder="' + esc(t('sourceMatPlaceholder', 'e.g. Inconel 718, 7075-T6 aluminum, A2 tool steel')) + '" id="rfq-source-mat">' +
        '<label class="rfq-field__label" for="rfq-source-mat">' + esc(t('materialLabel', 'Material')) + ' <span class="rfq-field__req">*</span></label>' +
      '</div>' +
    '</div>';

    html += '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
      '<div class="rfq-field">' +
        '<input type="text" data-rfq-action="set-sourcing-shape" value="' + esc(line.sourcingShape || '') + '" placeholder="' + esc(t('sourceShapePlaceholder', 'e.g. Round bar, Plate, Tube, Angle')) + '" id="rfq-source-shape">' +
        '<label class="rfq-field__label" for="rfq-source-shape">' + esc(t('shapeLabel', 'Shape')) + ' <span class="rfq-field__req">*</span></label>' +
      '</div>' +
    '</div>';

    html += '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
      '<div class="rfq-field">' +
        '<input type="text" data-rfq-action="set-sourcing-dimensions" value="' + esc(line.sourcingDimensions || '') + '" placeholder="' + esc(t('sourceDimsPlaceholder', 'e.g. 4" OD × 0.25" wall × 8 ft long, or 0.25" × 12" × 24"')) + '" id="rfq-source-dims">' +
        '<label class="rfq-field__label" for="rfq-source-dims">' + esc(t('dimensionsLabel', 'Dimensions')) + ' <span class="rfq-field__req">*</span></label>' +
      '</div>' +
    '</div>';

    html += '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
      '<div class="rfq-field">' +
        '<input type="text" data-rfq-action="set-sourcing-grade" value="' + esc(line.sourcingGrade || '') + '" placeholder="' + esc(t('sourceGradePlaceholder', 'e.g. T6, A36, 4140 annealed')) + '" id="rfq-source-grade">' +
        '<label class="rfq-field__label" for="rfq-source-grade">' + esc(t('gradeAlloyLabel', 'Grade / alloy')) + ' <span class="rfq-field__hint">' + esc(t('optional', 'optional')) + '</span></label>' +
      '</div>' +
    '</div>';

    html += '<div class="rfq-fieldgrid rfq-fieldgrid--2" style="margin-bottom:12px;">' +
      '<div class="rfq-field">' +
        '<input type="text" data-rfq-action="set-length" inputmode="decimal" placeholder="36" value="' + esc(line.length || '') + '" id="rfq-source-length">' +
        '<label class="rfq-field__label" for="rfq-source-length">' + esc(t('lengthPerPiece', 'Length per piece')) + ' <span class="rfq-field__req">*</span></label>' +
        '<span class="rfq-field__suffix">' + esc(t('unitIn', 'in')) + '</span>' +
      '</div>' +
      '<div class="rfq-qty-row" style="margin-top: 0;">' +
        '<div class="rfq-qty-wrap">' +
          '<button class="rfq-qty-btn" type="button" data-rfq-action="qty-dec">−</button>' +
          '<input class="rfq-qty-field" type="number" min="1" data-rfq-action="set-qty" value="' + (line.quantity || 1) + '">' +
          '<button class="rfq-qty-btn" type="button" data-rfq-action="qty-inc">+</button>' +
        '</div>' +
        '<div class="rfq-qty-desc">' + esc(t('qtyShort', 'qty')) + '</div>' +
      '</div>' +
    '</div>';

    html += '<div class="rfq-fieldset" style="margin-bottom:0;">' +
      '<div class="rfq-field">' +
        '<textarea rows="3" data-rfq-action="set-sourcing-notes" id="rfq-source-notes" placeholder="' + esc(t('sourceNotesPlaceholder', 'Tolerances, finish, mill cert, packaging, lead-time constraints, drawing reference, anything else worth noting…')) + '">' + esc(line.sourcingDescription || '') + '</textarea>' +
        '<label class="rfq-field__label" for="rfq-source-notes">' + esc(t('additionalDetail', 'Additional detail')) + ' <span class="rfq-field__hint">' + esc(t('optional', 'optional')) + '</span></label>' +
      '</div>' +
    '</div>';

    html += '<div class="rfq-actions">' +
      '<button class="rfq-btn rfq-btn--primary" type="button" data-rfq-action="commit-line">' + esc(t('addSourcingRequest', 'Add Sourcing Request')) + '</button>' +
      '<button class="rfq-btn rfq-btn--text" type="button" data-rfq-action="cancel-sourcing">' + esc(t('cancel', 'Cancel')) + '</button>' +
    '</div>';

    DOM.build.innerHTML = html;
    bindBuildActions();
    bindFieldFocus(DOM.build);
  }

  // ---------- customer info ----------
  function renderCustomer() {
    if (!DOM.customerBody) return;
    var c = state.customer;
    var html = '';

    if (state.user) {
      html += '<div class="rfq-signed">' +
        '<span class="rfq-signed__avatar">' + esc(initials(c.first_name, c.last_name, c.email)) + '</span>' +
        '<div>' +
          '<div class="rfq-signed__name">' + esc((c.first_name + ' ' + c.last_name).trim() || c.email) + '</div>' +
          '<span class="rfq-signed__email">' + esc(c.email) + '</span>' +
        '</div>' +
      '</div>';
    }

    html += '<div class="rfq-fieldgrid rfq-fieldgrid--2">' +
      '<div class="rfq-field"><input type="text" data-c="first_name" value="' + esc(c.first_name) + '" id="rfq-fname"><label class="rfq-field__label" for="rfq-fname">' + esc(t('firstNameLabel', 'First name')) + ' <span class="rfq-field__req">*</span></label></div>' +
      '<div class="rfq-field"><input type="text" data-c="last_name" value="' + esc(c.last_name) + '" id="rfq-lname"><label class="rfq-field__label" for="rfq-lname">' + esc(t('lastNameLabel', 'Last name')) + ' <span class="rfq-field__req">*</span></label></div>' +
      '<div class="rfq-field"><input type="email" data-c="email" value="' + esc(c.email) + '" id="rfq-email"><label class="rfq-field__label" for="rfq-email">' + esc(t('emailLabel', 'Email')) + ' <span class="rfq-field__req">*</span></label></div>' +
      '<div class="rfq-field"><input type="tel" data-c="phone" value="' + esc(c.phone) + '" placeholder="' + esc(t('optionalPlaceholder', 'optional')) + '" id="rfq-phone"><label class="rfq-field__label" for="rfq-phone">' + esc(t('phoneLabel', 'Phone')) + ' <span class="rfq-field__hint">' + esc(t('optional', 'optional')) + '</span></label></div>' +
    '</div>';

    html += '<div class="rfq-fieldgrid rfq-fieldgrid--2" style="margin-top: 12px;">' +
      '<div class="rfq-field"><input type="text" data-c="business_name" value="' + esc(c.business_name) + '" placeholder="' + esc(t('optionalPlaceholder', 'optional')) + '" id="rfq-biz"><label class="rfq-field__label" for="rfq-biz">' + esc(t('businessLabel', 'Business')) + ' <span class="rfq-field__hint">' + esc(t('optional', 'optional')) + '</span></label></div>' +
      '<div class="rfq-field"><select data-c="ship_to_province" id="rfq-prov">';
    html += '<option value="">' + esc(t('pickOne', 'Pick one…')) + '</option>';
    [
      ['Nova Scotia', t('provNS', 'Nova Scotia')],
      ['New Brunswick', t('provNB', 'New Brunswick')],
      ['Prince Edward Island', t('provPE', 'Prince Edward Island')],
      ['Newfoundland and Labrador', t('provNL', 'Newfoundland and Labrador')],
      ['Quebec', t('provQC', 'Quebec')],
      ['Ontario', t('provON', 'Ontario')],
      ['Manitoba', t('provMB', 'Manitoba')],
      ['Saskatchewan', t('provSK', 'Saskatchewan')],
      ['Alberta', t('provAB', 'Alberta')],
      ['British Columbia', t('provBC', 'British Columbia')],
      ['Yukon', t('provYT', 'Yukon')],
      ['Northwest Territories', t('provNT', 'Northwest Territories')],
      ['Nunavut', t('provNU', 'Nunavut')],
      ['Outside Canada', t('provOutside', 'Outside Canada')]
    ].forEach(function (p) {
      html += '<option value="' + esc(p[0]) + '"' + (c.ship_to_province === p[0] ? ' selected' : '') + '>' + esc(p[1]) + '</option>';
    });
    html += '</select><label class="rfq-field__label" for="rfq-prov">' + esc(t('provinceLabel', 'Province')) + ' <span class="rfq-field__req">*</span></label></div>' +
    '</div>';

    // Reassurance, not chrome: people abandon long forms when they fear
    // losing work. Saying we hold it is honest (local draft + server lead).
    html += '<p class="rfq-customer__fine">' + esc(t('customerFine', 'Your progress is saved as you go. Step away anytime and pick it back up.')) + '</p>';

    DOM.customerBody.innerHTML = html;
    bindCustomerActions();
    bindFieldFocus(DOM.customerBody);
  }

  function initials(f, l, e) {
    var s = ((f || '').charAt(0) + (l || '').charAt(0)).toUpperCase();
    return s || ((e || 'U').charAt(0).toUpperCase());
  }

  // ---------- delivery & timing ----------
  function renderSubmission() {
    if (!DOM.submissionBody) return;
    var s = state.submission;
    var html = '';

    html += '<div class="rfq-fieldset">' +
      '<span class="rfq-fieldset__label">' + esc(t('whenNeedIt', 'When do you need it?')) + ' <span class="rfq-field__req">*</span></span>' +
      '<div class="rfq-chips" data-rfq-timeline>';
    [
      ['Flexible', t('tlFlexible', 'Flexible')],
      ['1 week', t('tl1Week', '1 week')],
      ['2 weeks', t('tl2Weeks', '2 weeks')],
      ['By date', t('tlByDate', 'By date')]
    ].forEach(function (tl) {
      html += '<button type="button" class="rfq-chip ' + (s.timeline === tl[0] ? 'is-active' : '') + '" data-value="' + esc(tl[0]) + '">' +
        '<span class="rfq-chip__title">' + esc(tl[1]) + '</span>' +
      '</button>';
    });
    html += '</div></div>';

    if (s.timeline === 'By date') {
      html += '<div class="rfq-fieldset"><div class="rfq-field">' +
        '<input type="date" data-s="specific_date" value="' + esc(s.specific_date) + '" id="rfq-date">' +
        '<label class="rfq-field__label" for="rfq-date">' + esc(t('dateLabel', 'Date')) + '</label>' +
      '</div></div>';
    }

    html += '<div class="rfq-fieldset">' +
      '<span class="rfq-fieldset__label">' + esc(t('pickupOrShip', 'Pickup or ship?')) + ' <span class="rfq-field__req">*</span></span>' +
      '<div class="rfq-chips" data-rfq-fulfillment>' +
        '<button type="button" class="rfq-chip ' + (s.fulfillment_method === 'pickup' ? 'is-active' : '') + '" data-value="pickup">' +
          '<span class="rfq-chip__title">' + esc(t('pickupTitle', 'Pickup at shop')) + '</span>' +
          '<span class="rfq-chip__sub">' + esc(t('pickupSub', 'Mon-Fri 7:30-5')) + '</span>' +
        '</button>' +
        '<button type="button" class="rfq-chip ' + (s.fulfillment_method === 'include_shipping' ? 'is-active' : '') + '" data-value="include_shipping">' +
          '<span class="rfq-chip__title">' + esc(t('shipTitle', 'Ship to me')) + '</span>' +
          '<span class="rfq-chip__sub">' + esc(t('shipSub', 'Carrier quote included')) + '</span>' +
        '</button>' +
      '</div></div>';

    if (s.fulfillment_method === 'include_shipping') {
      var a = s.shipping_address || {};
      var PROVINCES = [
        ['Alberta', t('provAB', 'Alberta')],
        ['British Columbia', t('provBC', 'British Columbia')],
        ['Manitoba', t('provMB', 'Manitoba')],
        ['New Brunswick', t('provNB', 'New Brunswick')],
        ['Newfoundland and Labrador', t('provNL', 'Newfoundland and Labrador')],
        ['Northwest Territories', t('provNT', 'Northwest Territories')],
        ['Nova Scotia', t('provNS', 'Nova Scotia')],
        ['Nunavut', t('provNU', 'Nunavut')],
        ['Ontario', t('provON', 'Ontario')],
        ['Prince Edward Island', t('provPE', 'Prince Edward Island')],
        ['Quebec', t('provQC', 'Quebec')],
        ['Saskatchewan', t('provSK', 'Saskatchewan')],
        ['Yukon', t('provYT', 'Yukon')],
        ['Other / outside Canada', t('provOutsideLong', 'Other / outside Canada')]
      ];

      html += '<div class="rfq-fieldset">' +
        '<span class="rfq-fieldset__label">' + esc(t('shippingAddress', 'Shipping address')) + ' <span class="rfq-field__req">*</span></span>' +
        '<div class="rfq-fieldgrid rfq-fieldgrid--2" style="margin-bottom:12px;">' +
          '<div class="rfq-field"><input type="text" data-sa="first_name" autocomplete="given-name" value="' + esc(a.first_name) + '" id="rfq-sa-fn"><label class="rfq-field__label" for="rfq-sa-fn">' + esc(t('firstNameLabel', 'First name')) + ' <span class="rfq-field__req">*</span></label></div>' +
          '<div class="rfq-field"><input type="text" data-sa="last_name" autocomplete="family-name" value="' + esc(a.last_name) + '" id="rfq-sa-ln"><label class="rfq-field__label" for="rfq-sa-ln">' + esc(t('lastNameLabel', 'Last name')) + ' <span class="rfq-field__req">*</span></label></div>' +
        '</div>' +
        '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
          '<div class="rfq-field"><input type="text" data-sa="company" autocomplete="organization" value="' + esc(a.company) + '" placeholder="' + esc(t('optionalPlaceholder', 'optional')) + '" id="rfq-sa-co"><label class="rfq-field__label" for="rfq-sa-co">' + esc(t('companyLabel', 'Company')) + ' <span class="rfq-field__hint">' + esc(t('optional', 'optional')) + '</span></label></div>' +
        '</div>' +
        '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
          '<div class="rfq-field"><input type="text" data-sa="address_line_1" autocomplete="address-line1" value="' + esc(a.address_line_1) + '" placeholder="123 Main St" id="rfq-sa-a1"><label class="rfq-field__label" for="rfq-sa-a1">' + esc(t('addressLabel', 'Address')) + ' <span class="rfq-field__req">*</span></label></div>' +
        '</div>' +
        '<div class="rfq-fieldset" style="margin-bottom:12px;">' +
          '<div class="rfq-field"><input type="text" data-sa="address_line_2" autocomplete="address-line2" value="' + esc(a.address_line_2) + '" placeholder="' + esc(t('address2Placeholder', 'Unit, suite, floor, buzzer code…')) + '" id="rfq-sa-a2"><label class="rfq-field__label" for="rfq-sa-a2">' + esc(t('address2Label', 'Apartment, suite, etc.')) + ' <span class="rfq-field__hint">' + esc(t('optional', 'optional')) + '</span></label></div>' +
        '</div>' +
        '<div class="rfq-fieldgrid rfq-fieldgrid--3" style="margin-bottom:12px;">' +
          '<div class="rfq-field"><input type="text" data-sa="city" autocomplete="address-level2" value="' + esc(a.city) + '" placeholder="Toronto" id="rfq-sa-city"><label class="rfq-field__label" for="rfq-sa-city">' + esc(t('cityLabel', 'City')) + ' <span class="rfq-field__req">*</span></label></div>' +
          '<div class="rfq-field"><select data-sa="province" autocomplete="address-level1" id="rfq-sa-prov"><option value="">' + esc(t('selectPlaceholder', 'Select…')) + '</option>' +
            PROVINCES.map(function (p) { return '<option value="' + esc(p[0]) + '"' + (a.province === p[0] ? ' selected' : '') + '>' + esc(p[1]) + '</option>'; }).join('') +
          '</select><label class="rfq-field__label" for="rfq-sa-prov">' + esc(t('provinceLabel', 'Province')) + ' <span class="rfq-field__req">*</span></label></div>' +
          '<div class="rfq-field"><input type="text" data-sa="postal_code" autocomplete="postal-code" value="' + esc(a.postal_code) + '" placeholder="K1A 0B1" style="text-transform:uppercase;" id="rfq-sa-pc"><label class="rfq-field__label" for="rfq-sa-pc">' + esc(t('postalLabel', 'Postal code')) + ' <span class="rfq-field__req">*</span></label></div>' +
        '</div>' +
        '<div class="rfq-fieldset" style="margin-bottom:0;">' +
          '<div class="rfq-field"><select data-sa="country" autocomplete="country" id="rfq-sa-country">' +
            [['Canada', t('countryCanada', 'Canada')], ['United States', t('countryUS', 'United States')], ['Other', t('countryOther', 'Other')]].map(function (cn) { return '<option value="' + esc(cn[0]) + '"' + ((a.country || 'Canada') === cn[0] ? ' selected' : '') + '>' + esc(cn[1]) + '</option>'; }).join('') +
          '</select><label class="rfq-field__label" for="rfq-sa-country">' + esc(t('countryLabel', 'Country')) + ' <span class="rfq-field__req">*</span></label></div>' +
        '</div>' +
      '</div>';
    }

    html += '<div class="rfq-fieldset"><div class="rfq-field">' +
      '<textarea rows="3" data-s="notes" id="rfq-notes" placeholder="' + esc(t('notesPlaceholder', 'Project context, tolerance, edge prep, mill cert needs, packaging, recurring vs. one-time…')) + '">' + esc(s.notes) + '</textarea>' +
      '<label class="rfq-field__label" for="rfq-notes">' + esc(t('notesLabel', 'Notes')) + ' <span class="rfq-field__hint">' + esc(t('optional', 'optional')) + '</span></label>' +
    '</div></div>';

    // Drawings / spec sheets (RFQ v2). Files ride the submission as
    // multipart; the dashboard shows them on the quote's timeline.
    html += '<div class="rfq-fieldset">' +
      '<span class="rfq-fieldset__label">' + esc(t('drawingsLabel', 'Drawings or spec sheets')) + ' <span class="rfq-field__hint">' + esc(t('drawingsHint', 'optional · up to 5 files, 10 MB each')) + '</span></span>' +
      '<label class="rfq-attach">' +
        '<input type="file" data-rfq-files multiple hidden accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.dwg,.dxf,.step,.stp,.iges,.igs,.xls,.xlsx,.csv,.txt,.zip">' +
        '<span class="rfq-attach__btn">' + esc(t('attachBtn', '+ Attach files')) + '</span>' +
        '<span class="rfq-attach__hint">' + esc(t('attachHint', 'PDF, photos, DWG / DXF / STEP')) + '</span>' +
      '</label>' +
      '<ul class="rfq-upload__list" data-rfq-file-list></ul>' +
    '</div>';

    html += '<details class="rfq-disclose">' +
      '<summary class="rfq-disclose__summary">' + esc(t('advancedSummary', 'Advanced (mill cert, recurring)')) + '</summary>' +
      '<div class="rfq-disclose__body">' +
        '<label class="rfq-terms"><input type="checkbox" data-s="cert_required"' + (s.cert_required ? ' checked' : '') + '><span>' + esc(t('certCheckbox', 'I need a mill certification with this order')) + '</span></label>' +
        '<label class="rfq-terms"><input type="checkbox" data-s="recurring"' + (s.recurring ? ' checked' : '') + '><span>' + esc(t('recurringCheckbox', 'This is a recurring need (monthly, quarterly, etc.)')) + '</span></label>' +
      '</div>' +
    '</details>';

    DOM.submissionBody.innerHTML = html;
    bindSubmissionActions();
    bindFieldFocus(DOM.submissionBody);
    bindFileAttach();
  }

  // ---------- file attach (RFQ v2) ----------
  var FILE_MAX_BYTES = 10 * 1024 * 1024;
  var FILE_MAX_COUNT = 5;
  var FILE_EXT_RE = /\.(pdf|png|jpe?g|gif|webp|heic|dwg|dxf|step|stp|iges|igs|xlsx?|csv|txt|zip)$/i;

  function bindFileAttach() {
    var input = DOM.submissionBody.querySelector('[data-rfq-files]');
    if (!input) return;
    input.addEventListener('change', function () {
      var picked = Array.prototype.slice.call(input.files || []);
      var rejected = [];
      picked.forEach(function (f) {
        if (state.files.length >= FILE_MAX_COUNT) { rejected.push(f.name + ' ' + t('fileMaxCount', '(max {max} files)', { max: FILE_MAX_COUNT })); return; }
        if (f.size > FILE_MAX_BYTES) { rejected.push(f.name + ' ' + t('fileOverSize', '(over 10 MB)')); return; }
        if (!FILE_EXT_RE.test(f.name)) { rejected.push(f.name + ' ' + t('fileBadType', '(file type not supported)')); return; }
        state.files.push(f);
      });
      input.value = '';
      renderFileList();
      if (rejected.length) showError(t('filesRejected', 'Some files were not attached: {list}', { list: rejected.join(', ') }));
      else clearError();
    });
    renderFileList();
  }

  function renderFileList() {
    var list = DOM.submissionBody && DOM.submissionBody.querySelector('[data-rfq-file-list]');
    if (!list) return;
    list.innerHTML = state.files.map(function (f, i) {
      var kb = Math.round(f.size / 1024);
      var sizeStr = kb > 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB';
      return '<li>' + esc(f.name) + ' <span style="color:var(--rfq-mid);font-size:12px;">' + sizeStr + '</span>' +
        ' <button type="button" data-rfq-file-remove="' + i + '" style="background:none;border:none;color:var(--rfq-red);cursor:pointer;font-size:14px;line-height:1;" aria-label="' + esc(t('removeFileAria', 'Remove {name}', { name: f.name })) + '">×</button></li>';
    }).join('');
    list.querySelectorAll('[data-rfq-file-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.files.splice(parseInt(btn.getAttribute('data-rfq-file-remove'), 10), 1);
        renderFileList();
      });
    });
  }

  // ---------- step completion states (RFQ v2 polish) ----------
  // The numbered step bars turn into green checks as each step completes,
  // so "where am I / what's left" is readable at a glance.
  function updateStepBars() {
    var s = state.submission;
    var needsAddress = s.fulfillment_method === 'include_shipping' && !shippingAddressComplete(s.shipping_address);
    var done = {
      lines: state.lines.length > 0,
      contact: customerValid(),
      delivery: !!(s.timeline && s.fulfillment_method && !needsAddress),
      submit: false, // flips on actual submission (success panel takes over)
    };
    ROOT.querySelectorAll('[data-rfq-step]').forEach(function (el) {
      var key = el.getAttribute('data-rfq-step');
      var isDone = !!done[key];
      el.classList.toggle('is-done', isDone);
      if (isDone) {
        if (!el.getAttribute('data-rfq-step-num')) el.setAttribute('data-rfq-step-num', el.textContent);
        el.textContent = '✓';
      } else if (el.getAttribute('data-rfq-step-num')) {
        el.textContent = el.getAttribute('data-rfq-step-num');
      }
    });
  }

  // ---------- submit zone state ----------
  function renderSubmitState() {
    if (!DOM.submitBtn || !DOM.submitNote) return;
    updateStepBars();
    var s = state.submission;
    var needsAddress = s.fulfillment_method === 'include_shipping' && !shippingAddressComplete(s.shipping_address);
    var ready = state.lines.length > 0 &&
      customerValid() &&
      s.timeline &&
      s.fulfillment_method &&
      !needsAddress &&
      s.terms;
    DOM.submitBtn.disabled = !ready;
    if (DOM.stub) {
      var nLines = state.lines.length;
      DOM.stub.textContent = nLines
        ? tn('itemsReviewSendOne', 'itemsReviewSendOther', '{n} item · review & send', '{n} items · review & send', nLines)
        : t('reviewSend', 'Review & send');
    }

    // Compute single source of truth for the "what's next" hint.
    // Mobile sticky bar uses the short label; desktop submit recap uses the long sentence.
    var nItems = state.lines.length;
    var mobileLabel, recapHTML;
    if (state.lines.length === 0) {
      mobileLabel = t('mobileAddItem', 'Add an item');
      recapHTML = t('recapAddItem', '<strong>Add at least one item</strong> to continue.');
    } else if (!customerValid()) {
      mobileLabel = t('mobileAddContact', 'Add contact info');
      recapHTML = tn('recapContactOne', 'recapContactOther',
        'Sending <strong>{n} item</strong>. Fill in name, email, and province above to continue.',
        'Sending <strong>{n} items</strong>. Fill in name, email, and province above to continue.', nItems);
    } else if (!s.timeline || !s.fulfillment_method) {
      mobileLabel = t('mobilePickDelivery', 'Pick delivery');
      recapHTML = t('recapDelivery', 'Pick a <strong>timeline</strong> and <strong>delivery method</strong> above.');
    } else if (needsAddress) {
      mobileLabel = t('mobileAddAddress', 'Add address');
      recapHTML = t('recapAddress', 'Add a <strong>shipping address</strong> above (or switch to pickup).');
    } else if (!s.terms) {
      mobileLabel = t('mobileAcceptTerms', 'Accept terms');
      recapHTML = t('recapTerms', 'Accept the terms above to submit.');
    } else {
      mobileLabel = t('mobileSubmit', 'Submit Quote');
      recapHTML = tn('recapReadyOne', 'recapReadyOther',
        'Sending <strong>{n} item</strong> to <strong>{email}</strong>. We aim to respond within 2 business days.',
        'Sending <strong>{n} items</strong> to <strong>{email}</strong>. We aim to respond within 2 business days.', nItems)
        // token is {email} in EN, {courriel} in FR — fill whichever the active locale used.
        .split('{email}').join(esc(state.customer.email))
        .split('{courriel}').join(esc(state.customer.email));
    }
    DOM.submitNote.innerHTML = recapHTML;

    if (DOM.mobileBtn) {
      DOM.mobileBtn.textContent = mobileLabel;
      // Mobile bar stays clickable for incomplete states (scrolls to next step);
      // disabled only when there are zero items, since there's nothing to scroll to.
      DOM.mobileBtn.disabled = state.lines.length === 0;
      DOM.mobileBtn.classList.toggle('rfq-mobile__btn--ready', ready);
    }
  }

  // ---------- right-side cart (also serves as the line list everywhere) ----------
  function renderTicket() {
    if (!DOM.tkRows) return;

    if (!state.lines.length) {
      DOM.tkRows.innerHTML =
        '<div class="rfq-cart__empty">' +
          '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M3 5h2l3 12h11l3-9H6"/></svg>' +
          '<p style="margin: 0;">' + esc(t('cartEmpty', 'No items yet. Add your first product to get started.')) + '</p>' +
        '</div>';
    } else {
      var html = '';
      state.lines.forEach(function (l) {
        // Freeform rows render the customer's own words, verbatim.
        if (l.mode === 'freeform') {
          html += '<div class="rfq-cart__line rfq-cart__line--sourcing">' +
            '<div class="rfq-cart__thumb rfq-cart__thumb--sourcing">≡</div>' +
            '<div>' +
              '<p class="rfq-cart__title">' + esc((l.freeformText || t('listItem', 'List item')).slice(0, 70)) + '</p>' +
              '<div class="rfq-cart__detail">' + esc(t('fromList', "From your list, we'll confirm the details")) + '</div>' +
              '<button type="button" class="rfq-cart__edit" data-rfq-action="edit-line" data-id="' + esc(l.id) + '">' + esc(t('editBtn', 'Edit')) + '</button>' +
            '</div>' +
            '<button type="button" class="rfq-cart__remove" data-rfq-action="remove-line" data-id="' + esc(l.id) + '" aria-label="' + esc(t('removeAria', 'Remove')) + '">×</button>' +
          '</div>';
          return;
        }
        var sourcing = l.mode === 'sourcing';
        var code = sourcing
          ? (l.sourcingMaterial ? l.sourcingMaterial.charAt(0).toUpperCase() : '?')
          : (l.material ? codeFor(l.material.id, l.material.label) : '?');
        var name = sourcing
          ? t('sourcingPrefix', 'Sourcing:') + ' ' + ((l.sourcingMaterial || t('sourcingCustomWord', 'custom')) + (l.sourcingShape ? ' ' + l.sourcingShape : '')).trim()
          : (l.pinned && l.pinned.title) ? l.pinned.title
          : (l.material && l.shape && l.grade) ? (l.material.label + ' ' + l.shape.label + ' · ' + l.grade.label)
          : t('listItem', 'List item');
        var dimStr = sourcing
          ? (l.sourcingDimensions || '').slice(0, 60)
          : Object.keys(l.axis_values || {}).map(function (a) { return fmtDim(l.axis_values[a]); }).join(' × ');
        var qtyStr = l.quantity + (l.length ? ' × ' + l.length + '"' : '');
        if (!sourcing && l.width && l.length) qtyStr = l.quantity + ' × ' + l.width + '"×' + l.length + '"';
        html += '<div class="rfq-cart__line' + (sourcing ? ' rfq-cart__line--sourcing' : '') + '">' +
          '<div class="rfq-cart__thumb' + (sourcing ? ' rfq-cart__thumb--sourcing' : '') + '">' + esc(sourcing ? '?' : code) + '</div>' +
          '<div>' +
            '<p class="rfq-cart__title">' + esc(name) + '</p>' +
            '<div class="rfq-cart__detail"><strong>' + esc(qtyStr) + '</strong>' + (dimStr ? ' · ' + esc(dimStr) : '') + '</div>' +
            '<button type="button" class="rfq-cart__edit" data-rfq-action="edit-line" data-id="' + esc(l.id) + '">' + esc(t('editBtn', 'Edit')) + '</button>' +
          '</div>' +
          '<button type="button" class="rfq-cart__remove" data-rfq-action="remove-line" data-id="' + esc(l.id) + '" aria-label="' + esc(t('removeAria', 'Remove')) + '">×</button>' +
        '</div>';
      });
      // Save-progress nudge (v5 lead capture): once they're invested (≥1 line)
      // and we don't have an email yet, offer to hold the list. One field,
      // one button, dismissible by ignoring it.
      if (!state.customer.email) {
        html += '<div class="rfq-saveprog" data-rfq-saveprog>' +
          '<p class="rfq-saveprog__lead">' + esc(t('saveprogLead', 'Want us to hold your list?')) + '</p>' +
          '<div class="rfq-saveprog__row">' +
            '<input type="email" class="rfq-saveprog__input" data-rfq-saveprog-email placeholder="' + esc(t('saveprogPlaceholder', 'you@company.com')) + '" autocomplete="email" aria-label="' + esc(t('saveprogAria', 'Email to save progress')) + '">' +
            '<button type="button" class="rfq-saveprog__btn" data-rfq-saveprog-save>' + esc(t('saveprogSave', 'Save')) + '</button>' +
          '</div>' +
          '<p class="rfq-saveprog__fine">' + esc(t('saveprogFine', "We'll keep your progress so you can finish anytime, no account needed.")) + '</p>' +
        '</div>';
      }
      DOM.tkRows.innerHTML = html;
      DOM.tkRows.querySelectorAll('[data-rfq-action]').forEach(function (el) {
        el.addEventListener('click', function () {
          var a = el.getAttribute('data-rfq-action');
          var id = el.getAttribute('data-id');
          if (a === 'edit-line') editLine(id);
          else if (a === 'remove-line') removeLine(id);
        });
      });
      bindSaveProgress();
    }

    if (DOM.tkTotal) {
      if (state.lines.length === 0) {
        DOM.tkTotal.textContent = t('cartEmptyLabel', 'Empty');
        DOM.tkTotal.classList.add('rfq-cart__count--muted');
      } else {
        DOM.tkTotal.textContent = tn('itemOne', 'itemOther', '{n} item', '{n} items', state.lines.length);
        DOM.tkTotal.classList.remove('rfq-cart__count--muted');
      }
    }

    if (DOM.tkAddAnother) {
      DOM.tkAddAnother.hidden = state.lines.length === 0;
    }
  }

  function bindSaveProgress() {
    var box = DOM.tkRows && DOM.tkRows.querySelector('[data-rfq-saveprog]');
    if (!box) return;
    var input = box.querySelector('[data-rfq-saveprog-email]');
    var btn = box.querySelector('[data-rfq-saveprog-save]');
    if (!input || !btn) return;
    var save = function () {
      var v = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        box.classList.add('rfq-saveprog--invalid');
        input.focus();
        return;
      }
      box.classList.remove('rfq-saveprog--invalid');
      state.customer.email = v;
      renderCustomer();        // contact step inherits the email
      renderSubmitState();
      saveDraft();             // local + queued server mirror
      postServerDraft(false);  // and an immediate mirror — they just opted in
      box.innerHTML = '<p class="rfq-saveprog__done">' + esc(t('saveprogDone', "✓ Saved. Finish whenever you're ready. We'll hold your list.")) + '</p>';
    };
    btn.addEventListener('click', save);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); save(); } });
  }

  // ---------- bindings ----------
  function bindFieldFocus(scope) {
    if (!scope) return;
    scope.querySelectorAll('.rfq-field input, .rfq-field select, .rfq-field textarea').forEach(function (el) {
      el.addEventListener('focus', function () { var f = el.closest('.rfq-field'); if (f) f.classList.add('rfq-field--focused'); });
      el.addEventListener('blur',  function () { var f = el.closest('.rfq-field'); if (f) f.classList.remove('rfq-field--focused'); });
    });
  }

  function bindBuildActions() {
    DOM.build.querySelectorAll('[data-rfq-action]').forEach(function (el) {
      var a = el.getAttribute('data-rfq-action');
      if (a === 'pick-material') {
        el.addEventListener('change', function () { pickMaterial(el.value); });
      } else if (a === 'pick-shape') {
        el.addEventListener('change', function () { pickShape(el.value); });
      } else if (a === 'pick-combo') {
        el.addEventListener('change', function () { pickCombo(el.value); });
      } else if (a === 'pick-axis') {
        el.addEventListener('change', function () { setAxis(el.getAttribute('data-rfq-axis'), el.value); });
      } else if (a === 'pick-grade') {
        el.addEventListener('change', function () { pickGradeV2(el.value); });
      } else if (a === 'pick-product') {
        el.addEventListener('change', function () { pickProductByIndex(el.value); });
      } else if (a === 'set-length') {
        el.addEventListener('input', function () {
          if (!state.activeLine) return;
          state.activeLine.length = el.value;
          updateLengthGauge(state.activeLine);
          updateQtyDesc(state.activeLine);
        });
      } else if (a === 'set-width') {
        el.addEventListener('input', function () { if (state.activeLine) state.activeLine.width = el.value; });
      } else if (a === 'set-qty') {
        el.addEventListener('input', function () {
          var v = parseInt(el.value, 10);
          if (!state.activeLine) return;
          state.activeLine.quantity = isNaN(v) || v < 1 ? 1 : v;
          updateQtyDesc(state.activeLine);
        });
      } else if (a === 'set-sourcing-notes') {
        el.addEventListener('input', function () { if (state.activeLine) state.activeLine.sourcingDescription = el.value; });
      } else if (a === 'set-sourcing-material') {
        el.addEventListener('input', function () { if (state.activeLine) state.activeLine.sourcingMaterial = el.value; });
      } else if (a === 'set-sourcing-shape') {
        el.addEventListener('input', function () { if (state.activeLine) state.activeLine.sourcingShape = el.value; });
      } else if (a === 'set-sourcing-dimensions') {
        el.addEventListener('input', function () { if (state.activeLine) state.activeLine.sourcingDimensions = el.value; });
      } else if (a === 'set-sourcing-grade') {
        el.addEventListener('input', function () { if (state.activeLine) state.activeLine.sourcingGrade = el.value; });
      } else if (a === 'set-freeform') {
        el.addEventListener('input', function () { if (state.activeLine) state.activeLine.freeformText = el.value; });
      } else {
        el.addEventListener('click', handleBuildClick);
      }
    });
  }

  function handleBuildClick(e) {
    var t = e.currentTarget;
    var a = t.getAttribute('data-rfq-action');
    if (a === 'commit-line') commitLine();
    else if (a === 'qty-dec' || a === 'qty-inc') {
      if (!state.activeLine) return;
      var q = state.activeLine.quantity || 1;
      state.activeLine.quantity = a === 'qty-inc' ? q + 1 : Math.max(1, q - 1);
      var f = DOM.build.querySelector('[data-rfq-action="set-qty"]'); if (f) f.value = state.activeLine.quantity;
      updateQtyDesc(state.activeLine);
    }
    else if (a === 'reset-line') {
      state.activeLine = newLine();
      if (DOM.prefill) DOM.prefill.hidden = true; if (DOM.prefillStamp) DOM.prefillStamp.hidden = true;
      render();
    }
    else if (a === 'reset-sku') { renderSkuEmpty(); }
    else if (a === 'axes-continue') { if (state.activeLine) resolveAndAdvance(state.activeLine); }
    else if (a === 'goto-title-picker') {
      if (!state.activeLine) return;
      state.activeLine.titlePicker = true;
      state.activeLine.collision = null;
      render();
    }
    else if (a === 'back-to-axes') {
      if (!state.activeLine) return;
      state.activeLine.titlePicker = false;
      state.activeLine.collision = null;
      state.activeLine.products = [];
      render();
    }
    else if (a === 'goto-sourcing') { convertToSourcing('user-requested'); }
    else if (a === 'goto-freeform') {
      var fl = state.activeLine || (state.activeLine = newLine());
      fl.mode = 'freeform';
      fl.step = 'freeform';
      render();
    }
    else if (a === 'commit-freeform') { commitFreeformList(); }
    else if (a === 'cancel-sourcing') { state.activeLine = newLine(); render(); }
    else if (a === 'back-to-picker') {
      if (!state.activeLine) return;
      state.activeLine.material = null;
      state.activeLine.shape = null;
      state.activeLine.grade = null;
      state.activeLine.axis_values = {};
      state.activeLine.step = 'pick_material';
      state.activeLine.via = 'cascade';
      render();
    }
  }

  function bindCustomerActions() {
    DOM.customerBody.querySelectorAll('[data-c]').forEach(function (el) {
      var handler = function () { state.customer[el.getAttribute('data-c')] = el.value; renderSubmitState(); };
      el.addEventListener('input', handler);
      el.addEventListener('change', handler);
    });
    // Gentle inline validation on blur (RFQ v2 polish) — flags an obviously
    // bad email the moment they leave the field instead of at submit time.
    var emailEl = DOM.customerBody.querySelector('[data-c="email"]');
    if (emailEl) bindInlineValidation(emailEl, function (v) {
      return !v || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? '' : t('errEmailBad', "That email doesn't look right. Double-check it so your quote reaches you.");
    });
  }

  // Attach a validator to a field: on blur, show/hide a small hint line
  // under the field. Clears as soon as the value becomes valid.
  function bindInlineValidation(el, validate) {
    function run() {
      var msg = validate((el.value || '').trim());
      var field = el.closest('.rfq-field') || el.parentNode;
      var hint = field.querySelector('[data-rfq-inline-hint]');
      if (msg) {
        if (!hint) {
          hint = document.createElement('p');
          hint.setAttribute('data-rfq-inline-hint', '');
          hint.className = 'rfq-inline-hint';
          field.appendChild(hint);
        }
        hint.textContent = msg;
        field.classList.add('rfq-field--invalid');
      } else {
        if (hint) hint.remove();
        field.classList.remove('rfq-field--invalid');
      }
    }
    el.addEventListener('blur', run);
    el.addEventListener('input', function () {
      // Only clear eagerly — never nag mid-typing.
      var field = el.closest('.rfq-field') || el.parentNode;
      if (field.classList.contains('rfq-field--invalid')) run();
    });
  }

  function bindSubmissionActions() {
    DOM.submissionBody.querySelectorAll('[data-s]').forEach(function (el) {
      var handler = function () {
        var k = el.getAttribute('data-s');
        state.submission[k] = el.type === 'checkbox' ? el.checked : el.value;
        renderSubmitState();
      };
      el.addEventListener('input', handler);
      el.addEventListener('change', handler);
    });
    // Structured shipping-address inputs use data-sa="<key>"
    DOM.submissionBody.querySelectorAll('[data-sa]').forEach(function (el) {
      var handler = function () {
        var k = el.getAttribute('data-sa');
        if (!state.submission.shipping_address) state.submission.shipping_address = {};
        state.submission.shipping_address[k] = el.value;
        renderSubmitState();
      };
      el.addEventListener('input', handler);
      el.addEventListener('change', handler);
    });
    // Inline postal-code check (Canada only — other countries free-form).
    var pcEl = DOM.submissionBody.querySelector('[data-sa="postal_code"]');
    if (pcEl) bindInlineValidation(pcEl, function (v) {
      var country = (state.submission.shipping_address && state.submission.shipping_address.country) || 'Canada';
      if (!v || country !== 'Canada') return '';
      return /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(v) ? '' : t('errPostalBad', 'Canadian postal codes look like K1A 0B1.');
    });
    DOM.submissionBody.querySelectorAll('[data-rfq-timeline] .rfq-chip').forEach(function (b) {
      b.addEventListener('click', function () {
        state.submission.timeline = b.getAttribute('data-value');
        renderSubmission();
        renderSubmitState();
      });
    });
    DOM.submissionBody.querySelectorAll('[data-rfq-fulfillment] .rfq-chip').forEach(function (b) {
      b.addEventListener('click', function () {
        state.submission.fulfillment_method = b.getAttribute('data-value');
        // Re-render so the shipping-address textarea appears/disappears.
        renderSubmission();
        renderSubmitState();
      });
    });
  }

  // ---------- submit ----------
  // The single pinned product for a line, or null. NEVER an arbitrary
  // first-match: an ambiguous multi-match (stale legacy catalog) sends no
  // matched_product_id and staff resolve the product themselves.
  function pinnedProductOf(l) {
    if (l && l.products && l.products.length === 1 && l.products[0] && l.products[0].product) {
      return l.products[0].product;
    }
    return null;
  }

  // Catalog-mode line — payload v3.2.0 adds sku / handle / product_title
  // and sends the pinned product's FULL axis_values in dimensions.
  function catalogLineItem(l) {
    var pinned = pinnedProductOf(l);
    var dims = Object.assign({}, l.axis_values || {}, (pinned && pinned.axis_values) || {});
    if (l.length) { var lenN = parseInches(l.length); dims.length = isNaN(lenN) ? l.length : lenN; }
    if (l.width) { var widN = parseInches(l.width); dims.width = isNaN(widN) ? l.width : widN; }
    return {
      mode: l.mode,
      material: l.material ? l.material.label : '',
      shape: l.shape ? l.shape.label : '',
      grade: l.grade ? l.grade.label : '',
      dimensions: dims,
      quantity: l.quantity,
      sourcing_description: null,
      sourcing_files: [],
      is_specialty_freight: !!l.isSpecialtyFreight,
      is_always_raq: !!l.isAlwaysRaq,
      customer_unsure_fields: l.customerUnsureFields || [],
      matched_product_id: pinned ? (pinned.shopify_product_id || null) : null,
      sku: pinned ? (pinned.sm3_sku || null) : null,
      handle: pinned ? (pinned.handle || null) : null,
      product_title: pinned ? (pinned.title || null) : null,
    };
  }

  // Degraded prefill line (?product handle outside the catalog, pinned from
  // /products/<handle>.js). It never gets material/grade, and the backend
  // requires both for catalog mode at EVERY schema version, so a catalog
  // line here is a guaranteed 422 dead end. Send it as a freeform line with
  // the structured product identity instead — the exact shape the PDP quick
  // quote posts, which the backend composes ('TITLE (SKU N) - SIZE'),
  // persists (matched_product), and the staff email + dashboard already
  // render as product + size.
  function degradedLineItem(l) {
    var pin = l.pinned;
    var widN = parseInches(l.width);
    var lenN = parseInches(l.length);
    var wid = l.width ? (isNaN(widN) ? String(l.width).trim() + '"' : fmtDim(widN)) : '';
    var len = l.length ? (isNaN(lenN) ? String(l.length).trim() + '"' : fmtDim(lenN)) : '';
    var sizeText = (wid && len) ? (wid + ' x ' + len)
      : len ? ('cut length ' + len)
      : wid ? ('width ' + wid)
      : '';
    var title = pin.title || pin.handle || '';
    var skuPart = pin.sku ? ' (SKU ' + pin.sku + ')' : '';
    return {
      mode: 'freeform',
      freeform_text: title + skuPart + (sizeText ? ': ' + sizeText : ''),
      size_text: sizeText,
      quantity: l.quantity || 1,
      is_specialty_freight: !!l.isSpecialtyFreight,
      is_always_raq: !!l.isAlwaysRaq,
      customer_unsure_fields: l.customerUnsureFields || [],
      matched_product_id: pin.product_id || null,
      product_title: pin.title || null,
      product_sku: pin.sku || null,
      product_handle: pin.handle || null,
    };
  }

  function buildPayload() {
    var c = state.customer;
    var s = state.submission;
    var lineItems = state.lines.map(function (l) {
      if (l.mode === 'freeform') {
        // Verbatim pasted row — schema 3.1.0 additive mode. The ingest stores
        // the text on the line's sourcing_description; nothing is parsed.
        return {
          mode: 'freeform',
          freeform_text: (l.freeformText || '').trim(),
          quantity: l.quantity || 1,
          is_specialty_freight: false,
          is_always_raq: false,
          customer_unsure_fields: [],
          matched_product_id: null,
        };
      }
      if (l.mode === 'sourcing') {
        // Structured sourcing fields — free-text customer input. Dimensions
        // bundles the cross-section spec (text) + the per-piece cut length
        // so the dashboard can render both as part of the line summary.
        var lenN = parseInches(l.length);
        return {
          mode: 'sourcing',
          material: (l.sourcingMaterial || '').trim(),
          shape: (l.sourcingShape || '').trim(),
          grade: (l.sourcingGrade || '').trim(),
          dimensions: {
            text: (l.sourcingDimensions || '').trim(),
            length: isNaN(lenN) ? null : lenN,
          },
          quantity: l.quantity,
          sourcing_description: (l.sourcingDescription || '').trim() || null,
          sourcing_files: [],
          is_specialty_freight: !!l.isSpecialtyFreight,
          is_always_raq: !!l.isAlwaysRaq,
          customer_unsure_fields: l.customerUnsureFields || [],
          matched_product_id: null,
        };
      }
      // Degraded prefill: no catalog resolution, pinned straight from the
      // product JSON — goes out as freeform-with-identity, never catalog.
      if (l.degraded && l.pinned && !pinnedProductOf(l)) {
        return degradedLineItem(l);
      }
      return catalogLineItem(l);
    });
    // schema_version is evidence, not a constant. '3.2.0' promises every
    // catalog line was built from a v2 catalog shape (required_axes on the
    // line's shape snapshot), which is exactly what the backend's strict
    // axis validation assumes. Lines built any other way — the ~15 min
    // stale-CDN window serving the old v1 catalog (legacy combined
    // Size & Grade combo), a draft restored from the old form (7-day
    // retention), or "Add from your last request" history chips (no
    // expiry) — only carry the old whitelist dims, so stamping 3.2.0 on
    // them would 422 the whole submission. Those payloads go out as 3.1.0:
    // the backend keeps its lenient checks and staff still get the
    // missing_axes flags (computed for every catalog line, any version).
    // Sourcing/freeform lines carry no axis contract and degraded prefill
    // lines are emitted as freeform above, so only catalog lines vote.
    var allCatalogLinesV2 = state.lines.every(function (l) {
      if (l.mode !== 'catalog') return true;
      if (l.degraded && l.pinned) return true; // emitted via degradedLineItem
      return lineIsV2(l);
    });
    // Flat top-level shape — backend (lib/rfq-ingest.js validatePayload)
    // reads everything off the root, not nested under customer/submission/consent.
    var payload = {
      schema_version: allCatalogLinesV2 ? '3.2.0' : '3.1.0',
      submitted_from: state.prefill ? 'pdp' : 'dedicated',
      was_authenticated: !!state.user,
      lang: PAGE_LANG,
      // Customer
      email: c.email || '',
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      phone: c.phone || '',
      customer_status: c.customer_status || 'New',
      business_name: c.business_name || '',
      ship_to_province: c.ship_to_province || '',
      // Delivery & timing
      timeline: s.timeline || '',
      specific_date: s.specific_date || '',
      fulfillment_method: s.fulfillment_method || 'pickup',
      cert_requirement: s.cert_required ? 'Mill cert' : 'None',
      recurring: !!s.recurring,
      additional_notes: s.notes || '',
      // Consent
      terms_accepted: !!s.terms,
      terms_accepted_at: new Date().toISOString(),
      promotional_consent: false,
      // Items
      line_items: lineItems,
    };
    if (s.fulfillment_method === 'include_shipping' && shippingAddressComplete(s.shipping_address)) {
      var a = s.shipping_address;
      payload.shipping_address = {
        first_name: (a.first_name || '').trim(),
        last_name: (a.last_name || '').trim(),
        company: (a.company || '').trim(),
        address_line_1: (a.address_line_1 || '').trim(),
        address_line_2: (a.address_line_2 || '').trim(),
        city: (a.city || '').trim(),
        province: (a.province || '').trim(),
        postal_code: (a.postal_code || '').trim().toUpperCase(),
        country: (a.country || 'Canada').trim(),
      };
    }
    return payload;
  }

  function submitQuote() {
    if (DOM.submitBtn.disabled) return;
    if (!state.lines.length) return showError(t('errAddOne', 'Add at least one item.'));
    if (!customerValid()) return showError(t('errCustomerInfo', 'Customer info incomplete.'));
    if (!state.submission.terms) return showError(t('errAcceptTerms', 'Please accept the terms.'));

    DOM.submitBtn.disabled = true;
    DOM.submitBtn.innerHTML = t('submitting', 'Submitting…');
    clearError();

    // With attachments → multipart (payload JSON + files). Without → the
    // original JSON path, byte-identical to the pre-v2 form.
    var fetchOpts;
    if (state.files.length) {
      var fd = new FormData();
      fd.append('payload', JSON.stringify(buildPayload()));
      state.files.forEach(function (f) { fd.append('files', f, f.name); });
      fetchOpts = { method: 'POST', body: fd };
    } else {
      fetchOpts = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      };
    }
    fetch(INGEST_URL, fetchOpts)
      .then(function (r) {
        if (r.ok) return r.json();
        return r.text().then(function (t) {
          var body = null;
          try { body = JSON.parse(t); } catch (e) { /* non-JSON error page */ }
          var err = new Error('HTTP ' + r.status);
          err.status = r.status;
          err.body = body;
          throw err;
        });
      })
      .then(function (data) {
        if (data && data.reference_number) {
          // Request landed — clear the on-device draft, remember the lines
          // for next visit's "Add from your last request" chips. Server-side
          // lead draft is wiped by the backend; stop mirroring so a queued
          // timer can't re-create it.
          _serverDraftDisabled = true;
          if (_serverDraftTimer) { clearTimeout(_serverDraftTimer); _serverDraftTimer = null; }
          saveHistory(data.reference_number);
          lsDel(DRAFT_KEY);
          if (DOM.doneRef) DOM.doneRef.textContent = data.reference_number;
          if (DOM.stub) DOM.stub.textContent = t('submitted', 'Submitted');
          [DOM.builder, DOM.committed, DOM.customer, DOM.submission, DOM.submitZone].forEach(function (el) { if (el) el.hidden = true; });
          if (DOM.done) DOM.done.hidden = false;
          var url = '/pages/quote-confirmed?ref=' + encodeURIComponent(data.reference_number);
          // Signed tracking link (status page) — confirmation page renders a
          // "Track your request" button when present.
          if (data.status_url) url += '&track=' + encodeURIComponent(data.status_url);
          setTimeout(function () { window.location.href = url; }, 1400);
        } else {
          throw new Error('No reference number returned');
        }
      })
      .catch(function (err) {
        // Tell the customer what actually happened — and that nothing is
        // lost (the draft autosave keeps the request on this device).
        if (err && err.status === 422) {
          var details = err.body && err.body.details;
          var list = Array.isArray(details) ? details.slice(0, 4).join(' · ') : '';
          showError(t('err422', 'We could not accept some details{list}. Please review and try again, or call 902-468-1112.', { list: (list ? ': ' + list : '') }));
        } else if (err && err.status) {
          showError(t('errServer', 'Our system had a problem on our end. Your request is saved on this device, please try again in a minute, or call 902-468-1112.'));
        } else {
          showError(t('errConnection', 'Connection problem. Your request is saved on this device, check your internet and try again, or call 902-468-1112.'));
        }
        DOM.submitBtn.disabled = false;
        DOM.submitBtn.innerHTML = t('submitBtn', 'Submit Quote Request');
        console.error('[rfq] submit failed', err);
      });
  }

  // ---------- top-level event delegation for actions outside build zone ----------
  function bindGlobal() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-rfq-action]');
      if (!t || !ROOT.contains(t)) return;
      var a = t.getAttribute('data-rfq-action');
      // Mobile sticky bar — submit if ready, otherwise scroll to next unfinished step.
      if (a === 'mobile-cta') {
        var s = state.submission;
        var needsAddress = s.fulfillment_method === 'include_shipping' && !shippingAddressComplete(s.shipping_address);
        var ready = state.lines.length > 0 && customerValid() && s.timeline && s.fulfillment_method && !needsAddress && s.terms;
        if (ready) { submitQuote(); return; }
        var target = !state.lines.length ? DOM.builder
          : !customerValid() ? DOM.customer
          : (!s.timeline || !s.fulfillment_method || needsAddress) ? DOM.submission
          : !s.terms ? DOM.submitZone
          : DOM.submitZone;
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    // "+ Add another item" buttons (one in mobile, one in cart aside)
    ROOT.querySelectorAll('[data-rfq-add-line]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.activeLine = newLine();
        render();
        if (DOM.builder) DOM.builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    if (DOM.submitBtn) DOM.submitBtn.addEventListener('click', submitQuote);
    if (DOM.termsCheck) DOM.termsCheck.addEventListener('change', function () {
      state.submission.terms = DOM.termsCheck.checked;
      renderSubmitState();
    });
    if (DOM.prefillChange) DOM.prefillChange.addEventListener('click', function () {
      DOM.prefill.hidden = true;
      if (DOM.prefillStamp) DOM.prefillStamp.hidden = true;
      state.activeLine = null;
      render();
    });
  }

  // ---------- init ----------
  function init() {
    state.prefill = parsePrefill();
    hydrateCustomer();
    bindGlobal();

    if (window.MRU_RFQ_CATALOG) onCatalogReady();
    else {
      window.addEventListener('mru-rfq-catalog-loaded', onCatalogReady);
      window.addEventListener('mru-rfq-catalog-failed', function () {
        state.catalogStatus = 'failed';
        if (DOM.build) DOM.build.innerHTML = '<div class="rfq-loading" style="color: var(--rfq-red);">' + esc(t('catalogFailed', "Couldn't load the materials catalog. Please refresh, or call 902-468-1112.")) + '</div>';
      });
    }

    if (!state.activeLine && !state.lines.length) state.activeLine = newLine();
    render();

    // Draft autosave (RFQ v2): any interaction inside the form snapshots
    // state (debounced). A PDP prefill visit skips the restore offer — the
    // customer arrived with intent for a specific product.
    ROOT.addEventListener('input', saveDraftSoon);
    ROOT.addEventListener('click', saveDraftSoon);
    var draft = loadDraft();
    if (draft && !state.prefill) {
      offerDraftRestore(draft);
    } else {
      renderHistoryChips();
    }
  }

  function onCatalogReady() {
    state.catalog = window.MRU_RFQ_CATALOG;
    state.catalogStatus = 'ready';
    buildIndices();
    if (state.prefill) applyPrefill();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
